// voice interview with status
import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import { useNotification } from "../NotificationContext";
import avatarStreamService from "../utils/AvatarStreamService";
import {
  ensureSharedAudioGraph,
  getSharedAudioGraph,
  playBufferThroughSharedDestination,
  stopSharedAIPlayback,
} from "../utils/audioRouting";
import { createInterviewTurnState } from "../utils/interviewTurnState";
import { fetchInterviewDetails } from "../utils/api";
import { getInterviewLanguage } from "../utils/interviewLanguage";
import { monitorInterviewConnectivity } from "../utils/interviewConnectivity";
import { createNativeCapture } from "../utils/nativeCapture";

const WS_BASE = process.env.REACT_APP_WS_BASE;
const API_BASE = process.env.REACT_APP_TEMPLATE_BASE_URL;
const INTERNET_CONNECTIVITY_PROBE_URL = "https://www.gstatic.com/generate_204";

const UNKNOWN_SYSTEM_VALUE = "Unknown";
const RECONNECT_GRACE_SECONDS = 60;
const HEARTBEAT_INTERVAL_MS = 2000;
const HEARTBEAT_TIMEOUT_MS = 6000;
const SOCKET_CONNECT_TIMEOUT_MS = 8000;

const collectBrowserSystemInfo = async () => {
  const userAgent = navigator.userAgent || UNKNOWN_SYSTEM_VALUE;
  const platform = navigator.platform || UNKNOWN_SYSTEM_VALUE;
  const userAgentData = navigator.userAgentData;
  let brands = [];
  let uaDataValues = {};

  try {
    brands = userAgentData?.brands || [];
    if (userAgentData?.getHighEntropyValues) {
      uaDataValues = await userAgentData.getHighEntropyValues([
        "platform",
        "platformVersion",
        "fullVersionList",
      ]);
      brands = uaDataValues.fullVersionList || brands;
    }
  } catch (error) {
    // Browser detection is best-effort and must never interrupt the interview.
  }

  const brand = (name) => brands.find((item) => item.brand === name);
  const edge = brand("Microsoft Edge");
  const opera = brand("Opera");
  const chrome = brand("Google Chrome") || brand("Chrome");
  let browserName = UNKNOWN_SYSTEM_VALUE;
  let browserVersion = UNKNOWN_SYSTEM_VALUE;

  try {
    if (navigator.brave && typeof navigator.brave.isBrave === "function") {
      await navigator.brave.isBrave();
      browserName = "Brave";
    }
  } catch (error) {
    // Brave is not reliably detectable in every context; continue with UA data.
  }

  if (browserName !== "Brave" && edge) {
    browserName = "Edge";
    browserVersion = edge.version || browserVersion;
  } else if (browserName !== "Brave" && opera) {
    browserName = "Opera";
    browserVersion = opera.version || browserVersion;
  } else if (browserName !== "Brave" && chrome) {
    browserName = "Chrome";
    browserVersion = chrome.version || browserVersion;
  }

  const browserMatchers = [
    [/(?:Edg|EdgiOS|EdgA)\/([\d.]+)/, "Edge"],
    [/(?:OPR|Opera)\/([\d.]+)/, "Opera"],
    [/FxiOS\/([\d.]+)/, "Firefox"],
    [/Firefox\/([\d.]+)/, "Firefox"],
    [/CriOS\/([\d.]+)/, "Chrome"],
    [/Chrome\/([\d.]+)/, "Chrome"],
    [/Version\/([\d.]+).*Safari\//, "Safari"],
  ];
  const fallbackBrowser = browserMatchers.find(([matcher]) =>
    matcher.test(userAgent),
  );
  if (browserName === UNKNOWN_SYSTEM_VALUE && fallbackBrowser) {
    browserName = fallbackBrowser[1];
    browserVersion = userAgent.match(fallbackBrowser[0])?.[1] || browserVersion;
  }
  if (browserName === "Brave") {
    browserVersion =
      chrome?.version ||
      userAgent.match(/Chrome\/([\d.]+)/)?.[1] ||
      browserVersion;
  }

  const uaPlatform = uaDataValues.platform || userAgentData?.platform || "";
  let osName = UNKNOWN_SYSTEM_VALUE;
  let osVersion = UNKNOWN_SYSTEM_VALUE;
  if (uaPlatform) {
    osName = uaPlatform;
    // Chromium's platformVersion cannot reliably distinguish Windows 10 from
    // Windows 11, so do not log it as a Windows OS version.
    if (uaPlatform !== "Windows") {
      osVersion = uaDataValues.platformVersion || osVersion;
    }
  } else if (/Android/i.test(userAgent)) {
    osName = "Android";
    osVersion = userAgent.match(/Android\s([\d.]+)/i)?.[1] || osVersion;
  } else if (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1)
  ) {
    osName = "iOS/iPadOS";
    osVersion =
      userAgent
        .match(/(?:CPU (?:iPhone )?OS)\s([\d_]+)/i)?.[1]
        ?.replace(/_/g, ".") || osVersion;
  } else if (/Windows/i.test(userAgent)) {
    osName = "Windows";
  } else if (/Mac OS X/i.test(userAgent)) {
    osName = "macOS";
    osVersion =
      userAgent.match(/Mac OS X\s([\d_]+)/i)?.[1]?.replace(/_/g, ".") ||
      osVersion;
  } else if (/Linux/i.test(userAgent)) {
    osName = "Linux";
  }

  const isIOS =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const browserEngine = isIOS
    ? "WebKit"
    : /Firefox/i.test(userAgent)
      ? "Gecko"
      : /(?:Chrome|Chromium|Edg|OPR)/i.test(userAgent)
        ? "Blink"
        : /AppleWebKit/i.test(userAgent)
          ? "WebKit"
          : UNKNOWN_SYSTEM_VALUE;

  return {
    type: "interview_start_system_info",
    browserName,
    browserVersion,
    fullUserAgent: userAgent,
    browserVendor: navigator.vendor || UNKNOWN_SYSTEM_VALUE,
    browserEngine,
    osName,
    osVersion,
    platform,
  };
};

const BROWSER_TTS_GENDER_VOICES = {
  male: [
    "Microsoft David",
    "Microsoft Mark",
    "Microsoft Guy",
    "Microsoft Guy Online",
    "Microsoft Ryan",
    "Microsoft Andrew",
    "Microsoft Christopher",
    "Microsoft Brian",
  ],

  female: [
    "Microsoft Zira",
    "Microsoft Aria",
    "Microsoft Aria Online",
    "Microsoft Jenny",
    "Microsoft Sonia",
    "Microsoft Ava",
    "Microsoft Emma",
    "Microsoft Michelle",
  ],
};

let browserTtsVoicesCache = [];

const ConnectionOverlay = React.memo(({ state }) => {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 px-6 backdrop-blur-md"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-[460px] rounded-3xl bg-white px-8 py-8 text-center shadow-2xl ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10">
        {state === "reconnecting" && (
          <>
            {/* Wi-Fi / Connection Icon */}
            <div
              className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30"
              aria-hidden="true"
            >
              <div className="absolute inset-0 animate-ping rounded-full bg-blue-400/10" />

              <svg
                className="relative h-10 w-10 text-blue-600 dark:text-blue-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <path d="M12 20h.01" />
                <path d="M2 8.82a16 16 0 0 1 20 0" />
              </svg>
            </div>

            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-blue-600 dark:text-blue-400">
              Connection Issue
            </div>

            <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Connection interrupted
            </h2>

            <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-gray-600 dark:text-gray-300">
              We&apos;re trying to reconnect you. Your interview will resume
              automatically once the connection is restored.
            </p>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-gray-50 px-4 py-2 text-sm font-medium text-gray-600 dark:bg-gray-700/60 dark:text-gray-300">
              <span
                className="h-2 w-2 animate-pulse rounded-full bg-blue-500"
                aria-hidden="true"
              />
              Reconnecting...
            </div>
          </>
        )}

        {state === "restored" && (
          <>
            {/* Connection Restored Icon */}
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/30"
              aria-hidden="true"
            >
              <svg
                className="h-10 w-10 text-emerald-600 dark:text-emerald-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <path d="M12 20h.01" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>

            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">
              Connected
            </div>

            <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Connection restored
            </h2>

            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Connection restored successfully. Resuming your interview...
            </p>
          </>
        )}

        {state === "failed" && (
          <>
            {/* Connection Failed Icon */}
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/30"
              aria-hidden="true"
            >
              <svg
                className="h-10 w-10 text-red-600 dark:text-red-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <path d="M12 20h.01" />
                <path d="m8 8 8 8" />
                <path d="m16 8-8 8" />
              </svg>
            </div>

            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-red-600 dark:text-red-400">
              Connection Lost
            </div>

            <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Connection lost
            </h2>

            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Your connection was unavailable for more than one minute, so we
              can no longer continue this interview.
            </p>
          </>
        )}
      </div>
    </div>
  );
});

const VoiceInterview = forwardRef(
  (
    {
      interviewId,
      endInterview,
      onEndInterviewRequested,
      role = "candidate",
      onTranscript,
      onStatusChange,
      onMessagesChange,
      onCompilerEnabled,
      compilerEnabled = false,
      isPaused = false,
      interviewEnded = false,
    },
    ref,
  ) => {
    const { notify } = useNotification();
    const [messages, setMessages] = useState([]);
    const [audioPlaying, setAudioPlaying] = useState(false);
    const [flowError, setFlowError] = useState(null);
    const flowErrorRef = useRef(null);
    const closingTurnRef = useRef(null);
    const closingAudioStartedRef = useRef(false);
    const closingAckSentRef = useRef(false);
    const completionRequestedRef = useRef(false);
    const waitingGuardRef = useRef(null);
    const [usesGeminiLiveProvider, setUsesGeminiLiveProvider] = useState(false);
    const socketRef = useRef(null);
    const socketGenerationRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const heartbeatIntervalRef = useRef(null);
    const heartbeatSocketRef = useRef(null);
    const lastHeartbeatAckRef = useRef(0);
    const lastInterviewSocketTrafficAtRef = useRef(0);
    const heartbeatSequenceRef = useRef(0);
    const heartbeatPendingRef = useRef(null);
    const socketConnectTimeoutRef = useRef(null);
    const networkReachableRef = useRef(true);
    const codingHandoffRef = useRef(compilerEnabled);
    const reconnectInFlightRef = useRef(false);

    const sendEndInterviewDecision = (confirmed) => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        return;
      }
      socketRef.current.send(
        JSON.stringify({ type: "end_interview_decision", confirmed }),
      );
    };

    useImperativeHandle(ref, () => ({
      sendEndInterviewDecision,
    }));
    const recognitionRef = useRef(null);
    const interviewEndedRef = useRef(false);
    const recognitionStartAttemptsRef = useRef(0);
    // ✅ Fix 8: lock prevents duplicate SpeechRecognition.start() calls on Android
    const recognitionStartingRef = useRef(false);
    const audioRef = useRef(null);
    const activeAudioSourcesRef = useRef(new Set());
    const audioContextRef = useRef(null);
    const aiDestinationRef = useRef(null);
    const bufferRef = useRef("");
    const chunkBufferRef = useRef("");
    const pendingCommandRef = useRef(false);
    const pendingAnswerRef = useRef(false);
    const recognizingRef = useRef(false);
    const [status, setStatus] = useState("Interview Starting...");
    const statusRef = useRef("Interview Starting...");
    const [connectionUi, setConnectionUi] = useState({
      state: "connected",
      secondsRemaining: 0,
    });
    const reconnectUiIntervalRef = useRef(null);
    const reconnectGraceTimeoutRef = useRef(null);
    const restoredUiTimerRef = useRef(null);
    const connectionUiDeadlineRef = useRef(0);
    const connectionInterruptedRef = useRef(false);
    const restoreListeningAfterReconnectRef = useRef(false);
    const reconnectTimeoutFinalizingRef = useRef(false);
    const containerRef = useRef(null);
    const [countdown, setCountdown] = useState(0);
    const canReceiveQuestionRef = useRef(true);
    const silenceTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const isPausedRef = useRef(isPaused);
    const ttsDoneResolverRef = useRef(null);
    const questionQueueRef = useRef([]);
    const lastSentInterimRef = useRef("");
    const nextPlayTimeRef = useRef(0);
    const isPlayingStreamingRef = useRef(false);
    const audioQueueRef = useRef([]);
    const lastProcessedIndexRef = useRef(-1);
    const pendingQuestionRef = useRef("");
    const questionDisplayedRef = useRef(false);
    const ttsSafetyTimeoutRef = useRef(null);
    const turnStateRef = useRef(createInterviewTurnState());
    const activeTurnIdRef = useRef(null);
    const mediaGenerationRef = useRef(0);
    const audioPlaybackStartedRef = useRef(false);
    const audioStreamGenerationRef = useRef(0);
    const activeAudioStreamIdRef = useRef(null);
    const activeAudioTurnIdRef = useRef(null);
    const activeAudioQuestionRef = useRef("");
    const lastAudioStreamSequenceRef = useRef(0);
    const displayedTurnIdsRef = useRef(new Set());
    const activeQuestionTextRef = useRef("");
    const submittedTurnIdRef = useRef(null);
    const lastSubmittedAnswerRef = useRef(null);
    const reconnectAnswerRef = useRef(null);
    const recoveredAnswerTurnRef = useRef(null);
    const recoveryReplayTimerRef = useRef(null);
    const recoveryTurnRef = useRef(null);
    const currentTurnShouldListenRef = useRef(false);
    const preferredLangRef = useRef("en-US");
    const interviewLanguageRef = useRef(null);
    const languageReadyRef = useRef(false);
    const [languageReady, setLanguageReady] = useState(false);
    const scheduledSttProviderRef = useRef("browser");
    const nativeModeRef = useRef(false);
    const nativeCaptureRef = useRef(null);
    const nativeCaptureReadyRef = useRef(false);
    const nativePlaybackAckRef = useRef(null);
    const nativeListenAllowedRef = useRef(false);

    const isBrowserOnlyModeRef = useRef(false);
    const avatarModeRef = useRef("static"); // "did" | "static" — fetched at mount
    // 🔒 Prevent TTS voice selection before avatar gender is loaded
    const avatarConfigLoadedRef = useRef(false);
    const avatarGenderRef = useRef("male");
    const currentAudioProviderRef = useRef("legacy"); // "legacy" | "gemini"
    const isPlannedModeRef = useRef(false);
    const micStreamRef = useRef(null);
    const micSourceRef = useRef(null);
    const micProcessorRef = useRef(null);
    const geminiAudioChunksRef = useRef([]);
    const geminiSpeechActiveRef = useRef(false);
    const geminiSpeechEndTimeoutRef = useRef(null);
    const isGeminiLiveRef = useRef(null); // null = unknown, true = Gemini Live active, false = legacy
    const GEMINI_SILENCE_MS = 700;
    const GEMINI_VAD_THRESHOLD = 0.01;
    const geminiPlaybackStartTimeRef = useRef(0);
    const GEMINI_BATCH_SIZE = 10; // Fixed batch size matching legacy
    const geminiActiveBatchesRef = useRef(new Set());
    const geminiPcmQueueRef = useRef([]); // Dedicated queue for Gemini PCM chunks
    const geminiStreamStartedRef = useRef(false); // Whether playback has started for current stream

    // ✅ FIX #1: Track last displayed question to avoid duplicates
    const lastDisplayedQuestionRef = useRef("");
    // ✅ FIX #2: Track processed message IDs to avoid duplicates
    const processedMessageIdsRef = useRef(new Set());
    // ✅ FIX #3: Track if audio stream has been finalized
    const audioStreamFinalizedRef = useRef(false);
    const didSpeakingEndFallbackRef = useRef(null); // fallback timer for D-ID silence detection
    // 🍎 iOS Safari bug: speechSynthesis auto-pauses mid-utterance after ~15s.
    // This interval nudges it awake with pause()/resume() while it's speaking.
    const ttsKeepAliveIntervalRef = useRef(null);
    const browserTtsQueueRef = useRef([]);
    const browserTtsPlayingRef = useRef(false);
    const browserTtsVoicesRef = useRef([]);
    // 🔒 Lock one browser TTS voice for the entire interview
    const selectedBrowserTtsVoiceRef = useRef(null);
    const selectedBrowserTtsGenderRef = useRef(null);
    const recognitionStartedResolverRef = useRef(null); // ✅ Promise resolver for waiting on recognition start

    const trackAudioSource = (source) => {
      if (!source) return source;
      activeAudioSourcesRef.current.add(source);
      const remove = () => activeAudioSourcesRef.current.delete(source);
      source.addEventListener?.("ended", remove, { once: true });
      source.addEventListener?.("error", remove, { once: true });
      return source;
    };

    const stopTrackedAudioSources = () => {
      for (const source of activeAudioSourcesRef.current) {
        try {
          source.stop();
        } catch (error) {}
      }
      activeAudioSourcesRef.current.clear();
    };

    const getGenderMatchedBrowserVoice = (voices, gender, lang = "en-US") => {
      if (!voices || !voices.length) {
        return null;
      }

      const normalizedGender = gender === "female" ? "female" : "male";

      // 1. First priority: matching gender + English (en-US)
      const matchingGenderVoice = voices.find((voice) => {
        const voiceLang = (voice.lang || "").toLowerCase();

        if (voiceLang !== lang.toLowerCase()) {
          return false;
        }

        const name = (voice.name || "").toLowerCase();

        const isFemale =
          /female|zira|samantha|jenny|aria|susan|hazel|eva|sara/.test(name);

        const isMale = /male|mark|david|guy|ryan|george|daniel|alex/.test(name);

        return normalizedGender === "female" ? isFemale : isMale;
      });

      if (matchingGenderVoice) {
        console.log(
          `[BROWSER-TTS] Using gender-matched ${normalizedGender} voice:`,
          matchingGenderVoice.name,
        );

        return matchingGenderVoice;
      }

      // A language-level match is acceptable, but never cross language families.
      const languageCode = lang.split("-")[0].toLowerCase();
      return (
        voices.find(
          (voice) =>
            (voice.lang || "").toLowerCase().split("-")[0] === languageCode,
        ) || null
      );
    };

    const initializeBrowserTtsVoice = () => {
      if (!window.speechSynthesis) {
        console.warn("⚠️ [BROWSER-TTS] speechSynthesis unavailable");
        return null;
      }
      if (!languageReadyRef.current) {
        return null;
      }

      const gender = avatarGenderRef.current === "female" ? "female" : "male";

      // 🔒 Voice already selected for this interview
      if (
        selectedBrowserTtsVoiceRef.current &&
        selectedBrowserTtsGenderRef.current === gender
      ) {
        console.log("🔒 [BROWSER-TTS] Reusing locked voice:", {
          gender,
          voice: selectedBrowserTtsVoiceRef.current.name,
        });

        return selectedBrowserTtsVoiceRef.current;
      }

      const voices =
        browserTtsVoicesRef.current?.length > 0
          ? browserTtsVoicesRef.current
          : window.speechSynthesis.getVoices();

      const voice = getGenderMatchedBrowserVoice(
        voices,
        gender,
        preferredLangRef.current,
      );

      if (!voice) {
        console.error(
          `❌ [BROWSER-TTS] No browser TTS voice available at all`,
          {
            gender,
            language: preferredLangRef.current,
            availableVoices: voices.length,
          },
        );

        return null;
      }

      // 🔒 Lock selected OR fallback voice for entire interview
      selectedBrowserTtsVoiceRef.current = voice;
      selectedBrowserTtsGenderRef.current = gender;

      console.log("🔒 [BROWSER-TTS] Voice locked for interview:", {
        gender,
        name: voice.name,
        lang: voice.lang,
        voiceURI: voice.voiceURI,
        localService: voice.localService,
      });

      return voice;
    };

    useEffect(() => {
      let cancelled = false;

      const loadInterviewLanguage = async () => {
        try {
          const interview = await fetchInterviewDetails(interviewId, {
            strict: true,
          });
          let language;
          try {
            language = getInterviewLanguage(interview);
          } catch (error) {
            console.error(
              "[LANGUAGE] Scheduled language is unsupported",
              error,
            );
            notify(
              "error",
              "This interview language is not supported by the current runtime.",
            );
            return;
          }
          if (cancelled) return;
          interviewLanguageRef.current = language;
          preferredLangRef.current = language.locale;
          isGeminiLiveRef.current =
            interview?.interview_provider === "gemini_live";
          setUsesGeminiLiveProvider(isGeminiLiveRef.current);
          scheduledSttProviderRef.current =
            interview?.stt_provider || "browser";
          isBrowserOnlyModeRef.current =
            interview?.tts_provider === "browser" &&
            interview?.stt_provider === "browser";
          languageReadyRef.current = true;
          setLanguageReady(true);
          if (window.speechSynthesis?.getVoices().length) {
            initializeBrowserTtsVoice();
          }
          console.info("[LANGUAGE] Interview runtime language loaded", {
            interviewId,
            language: language.displayName,
            languageCode: language.languageCode,
            locale: language.locale,
          });
        } catch (error) {
          if (cancelled) return;
          languageReadyRef.current = false;
          setLanguageReady(false);
          notify(
            "error",
            "Unable to load the scheduled interview language. The interview cannot start.",
          );
          console.error(
            "[LANGUAGE] Scheduled language could not be loaded",
            error,
          );
        }
      };

      if (interviewId) loadInterviewLanguage();
      return () => {
        cancelled = true;
      };
    }, [interviewId]);

    const markGeminiAnswerReady = () => {
      if (flowErrorRef.current || (closingTurnRef.current === activeTurnIdRef.current && closingTurnRef.current)) return;
      if (isInterviewTransportInterrupted() || interviewEndedRef.current) return;
      if (activeTurnIdRef.current && submittedTurnIdRef.current === activeTurnIdRef.current) return;
      pendingAnswerRef.current = true;
      try {
        window.dispatchEvent(new CustomEvent("gemini_pending_answer"));
      } catch (e) {}
    };

    useEffect(() => {
      if (interviewEnded) {
        interviewEndedRef.current = true;
        console.log("🔒 VoiceInterview received interview-ended state");
      }
    }, [interviewEnded]);

    const clearTtsKeepAlive = () => {
      if (ttsKeepAliveIntervalRef.current) {
        clearInterval(ttsKeepAliveIntervalRef.current);
        ttsKeepAliveIntervalRef.current = null;
      }
    };

    const startTtsKeepAlive = () => {
      if (!isIOS) return;
      clearTtsKeepAlive();
      ttsKeepAliveIntervalRef.current = setInterval(() => {
        if (window.speechSynthesis && window.speechSynthesis.speaking) {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        } else {
          clearTtsKeepAlive();
        }
      }, 4000);
    };

    useEffect(() => {
      isMountedRef.current = true;

      const syncAudioRefs = () => {
        const shared = getSharedAudioGraph();
        if (shared) {
          audioContextRef.current = shared.audioContext;
          aiDestinationRef.current = shared.destination;
        }
      };

      syncAudioRefs();

      const handleGraphReady = () => {
        syncAudioRefs();
      };

      window.addEventListener("ai-audio-graph-ready", handleGraphReady);

      return () => {
        window.removeEventListener("ai-audio-graph-ready", handleGraphReady);
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      if (onStatusChange) onStatusChange(status);
    }, [status, onStatusChange]);

    // Fetch voice provider settings to determine if browser-only mode
    useEffect(() => {
      const fetchInterviewMode = async () => {
        try {
          const response = await fetch(`${API_BASE}/settings/interview-mode`);
          if (!response.ok) {
            throw new Error(`Interview mode fetch failed: ${response.status}`);
          }
          const data = await response.json();
          isPlannedModeRef.current = data?.mode === "planned";
          console.log("🎯 Interview mode:", data?.mode);
        } catch (error) {
          console.warn(
            "🎯 Could not fetch interview mode, defaulting to dynamic:",
            error,
          );
          isPlannedModeRef.current = false;
        }
      };
      const fetchVoiceProviderSettings = async () => {
        try {
          const response = await fetch(
            `${API_BASE}/voice_provider/voice-providers`,
          );
          if (response.ok) {
            const providers = await response.json();
            const activeStt = providers.find(
              (p) => p.type === "stt" && p.active,
            );
            const activeTts = providers.find(
              (p) => p.type === "tts" && p.active,
            );

            if (
              activeStt?.provider === "browser" &&
              activeTts?.provider === "browser"
            ) {
              isBrowserOnlyModeRef.current = true;
              console.log(
                "🎤 Browser-only audio mode enabled - text messages only",
              );
            } else {
              isBrowserOnlyModeRef.current = false;
            }
          }
        } catch (error) {
          console.error("Error fetching voice provider settings:", error);
          isBrowserOnlyModeRef.current = false;
        }
      };

      fetchInterviewMode();
      fetchVoiceProviderSettings();
    }, []);

    // Fetch avatar mode so audio_chunk routing knows whether we're in D-ID mode
    useEffect(() => {
      const fetchAvatarMode = async () => {
        try {
          const REST_BASE =
            process.env.REACT_APP_REST_BASE_URL || "http://localhost:8000";

          const res = await fetch(`${REST_BASE}/avatar/current-avatar`);

          if (!res.ok) {
            throw new Error(`Avatar config request failed: ${res.status}`);
          }

          const data = await res.json();

          avatarModeRef.current = data.avatar_mode || "static";

          avatarGenderRef.current =
            data.avatar_gender === "female" ? "female" : "male";

          avatarConfigLoadedRef.current = true;

          console.log("🎬 [VoiceInterview] Avatar configuration:", {
            mode: avatarModeRef.current,
            gender: avatarGenderRef.current,
          });

          // If browser voices are already available,
          // lock the matching voice now.
          if (
            window.speechSynthesis &&
            window.speechSynthesis.getVoices().length > 0
          ) {
            initializeBrowserTtsVoice();
          }
        } catch (e) {
          console.warn("🎬 [VoiceInterview] Could not fetch avatar mode:", e);
          avatarConfigLoadedRef.current = true;
          avatarModeRef.current = "static";
          avatarGenderRef.current = "male";

          if (
            window.speechSynthesis &&
            window.speechSynthesis.getVoices().length > 0
          ) {
            initializeBrowserTtsVoice();
          }
        }
      };

      fetchAvatarMode();
    }, []);

    useEffect(() => {
      const loadBrowserTtsVoices = () => {
        if (!window.speechSynthesis) {
          return;
        }

        const voices = window.speechSynthesis.getVoices() || [];

        browserTtsVoicesCache = voices;
        browserTtsVoicesRef.current = voices;

        console.log(
          "🔊 [BROWSER-TTS] Available voices:",
          voices.map((voice) => ({
            name: voice.name,
            lang: voice.lang,
            localService: voice.localService,
          })),
        );

        // 🔒 Lock interview voice once voices become available
        if (
          voices.length > 0 &&
          avatarConfigLoadedRef.current &&
          !selectedBrowserTtsVoiceRef.current
        ) {
          initializeBrowserTtsVoice();
        }
      };

      loadBrowserTtsVoices();

      if (window.speechSynthesis) {
        window.speechSynthesis.addEventListener(
          "voiceschanged",
          loadBrowserTtsVoices,
        );
      }

      return () => {
        if (window.speechSynthesis) {
          window.speechSynthesis.removeEventListener(
            "voiceschanged",
            loadBrowserTtsVoices,
          );
        }
      };
    }, []);
    const reconnectingStatusRef = useRef(false);
    const connectionUiStateRef = useRef("connected");

    const safeSetStatus = (s) => {
      if (!isMountedRef.current) return;
      // Receiving text, starting TTS generation, and scheduling an audio
      // buffer do not mean the candidate can hear it yet.
      if (s === "AI Speaking..." && avatarModeRef.current !== "custom" &&
          !audioPlaybackStartedRef.current) {
        s = "Please wait...";
      }
      // Cancellation can trigger late audio callbacks. Recovery owns the UI.
      if (connectionInterruptedRef.current &&
          (s === "AI Speaking..." || s === "Listening" || s === "Please wait...")) {
        s = "Reconnecting";
      }
      if (closingTurnRef.current && closingTurnRef.current === activeTurnIdRef.current &&
          ["AI Speaking...", "Listening", "Please wait...", "Completing interview..."].includes(s)) {
        s = "End Interview";
      }
      statusRef.current = s;
      if (s === "Reconnecting") {
        if (reconnectingStatusRef.current) return;
        reconnectingStatusRef.current = true;
      } else {
        reconnectingStatusRef.current = false;
      }
      setStatus(s);
      if (onStatusChange) onStatusChange(s);
    };

    // A browser SpeechRecognition instance can fire onend shortly after the
    // socket/network has gone away.  Do not let that callback restart the mic
    // or submit a stale answer while the interview transport is recovering.
    const isInterviewTransportInterrupted = () =>
      connectionInterruptedRef.current ||
      !networkReachableRef.current ||
      !socketRef.current ||
      socketRef.current.readyState !== WebSocket.OPEN;

    const isMediaGenerationCurrent = (generation) =>
      generation === mediaGenerationRef.current &&
      isMountedRef.current && !interviewEndedRef.current &&
      !isInterviewTransportInterrupted();

    const updateFlowError = (error) => {
      flowErrorRef.current = error;
      setFlowError(error);
    };

    const clearWaitingGuard = () => {
      clearTimeout(waitingGuardRef.current);
      waitingGuardRef.current = null;
    };

    const showInterviewFlowError = (error) => {
      if (error.turn_id && error.turn_id !== activeTurnIdRef.current) return;
      if (error.stream_id && activeAudioStreamIdRef.current &&
          error.stream_id !== activeAudioStreamIdRef.current) return;
      clearWaitingGuard();
      audioStreamGenerationRef.current += 1;
      stopSharedAIPlayback();
      stopTrackedAudioSources();
      geminiPcmQueueRef.current = [];
      geminiActiveBatchesRef.current.clear();
      isPlayingStreamingRef.current = false;
      window.__WAITING_FOR_TTS__ = false;
      markAudioPlaybackStarted(false);
      pendingAnswerRef.current = false;
      updateFlowError(error);
      const statusByCode = {
        WAITING_FOR_RESPONSE: "Interview response delayed",
        GEMINI_AUDIO_UNAVAILABLE: "AI audio was not generated",
        AUDIO_PLAYBACK_FAILED: "Audio playback failed",
        NATIVE_PLAYBACK_UNCONFIRMED: "Audio playback was not confirmed",
        GEMINI_NATIVE_FAILED: "Interview flow paused",
        MIC_UNAVAILABLE: "Microphone unavailable",
        FINALIZATION_FAILED: "Interview finalization failed",
      };
      safeSetStatus(statusByCode[error.code] || "Interview audio unavailable");
    };

    const armWaitingGuard = (turnId = activeTurnIdRef.current) => {
      clearWaitingGuard();
      waitingGuardRef.current = setTimeout(() => {
        waitingGuardRef.current = null;
        if (!isMountedRef.current || interviewEndedRef.current ||
            isInterviewTransportInterrupted() || audioPlaybackStartedRef.current ||
            turnId !== activeTurnIdRef.current) return;
        showInterviewFlowError({
          code: "WAITING_FOR_RESPONSE", turn_id: turnId,
          retryable: Boolean(turnId && submittedTurnIdRef.current !== turnId),
          message: "The interview is taking longer to respond. You can retry audio or keep waiting.",
        });
      }, 45000);
    };

    const markAudioPlaybackStarted = (playing) => {
      audioPlaybackStartedRef.current = playing;
      setAudioPlaying(playing);
      if (playing) {
        clearWaitingGuard();
        if (closingTurnRef.current === activeTurnIdRef.current) closingAudioStartedRef.current = true;
      }
    };

    const finishClosingPlayback = (turnId) => {
      if (!turnId || closingTurnRef.current !== turnId ||
          activeTurnIdRef.current !== turnId || !closingAudioStartedRef.current) return false;
      if (closingAckSentRef.current) return true;
      if (isInterviewTransportInterrupted()) return false;
      closingAckSentRef.current = true;
      clearWaitingGuard();
      pendingAnswerRef.current = false;
      window.__WAITING_FOR_TTS__ = false;
      socketRef.current.send(JSON.stringify({
        type: "assistant_playback_complete", turn_id: turnId,
        stream_id: activeAudioStreamIdRef.current,
        stream_sequence: lastAudioStreamSequenceRef.current,
      }));
      safeSetStatus("Completing interview...");
      return true;
    };

    const retryCurrentAudio = () => {
      if (isInterviewTransportInterrupted()) return;
      updateFlowError(null);
      closingAudioStartedRef.current = false;
      closingAckSentRef.current = false;
      audioStreamGenerationRef.current += 1;
      stopSharedAIPlayback();
      stopTrackedAudioSources();
      geminiPcmQueueRef.current = [];
      geminiActiveBatchesRef.current.clear();
      isPlayingStreamingRef.current = false;
      activeAudioStreamIdRef.current = null;
      markAudioPlaybackStarted(false);
      safeSetStatus("AI Speaking...");
      armWaitingGuard();
      socketRef.current.send(JSON.stringify({type: "retry_audio", turn_id: activeTurnIdRef.current}));
    };

    useEffect(() => () => clearTimeout(waitingGuardRef.current), []);

    const sendAnswerComplete = (text) => {
      if (nativeModeRef.current) return false;
      const normalized = typeof text === "string" ? text.trim() : "";
      const socket = socketRef.current;
      if (
        !normalized ||
        !pendingAnswerRef.current ||
        (activeTurnIdRef.current && submittedTurnIdRef.current === activeTurnIdRef.current) ||
        isInterviewTransportInterrupted() ||
        !socket ||
        socket.readyState !== WebSocket.OPEN
      ) {
        console.warn("[ANSWER] Final answer send skipped", {
          hasText: Boolean(normalized),
          hasSocket: Boolean(socket),
          turnId: activeTurnIdRef.current,
        });
        return false;
      }
      socket.send(
        JSON.stringify({
          type: "answer_complete",
          text: normalized,
          turn_id: activeTurnIdRef.current,
        }),
      );
      submittedTurnIdRef.current = activeTurnIdRef.current;
      lastSubmittedAnswerRef.current = {
        turnId: activeTurnIdRef.current,
        text: normalized,
      };
      armWaitingGuard();
      return true;
    };

    const clearConnectionUiTimers = () => {
      if (reconnectUiIntervalRef.current) {
        clearInterval(reconnectUiIntervalRef.current);
        reconnectUiIntervalRef.current = null;
      }
      if (reconnectGraceTimeoutRef.current) {
        clearTimeout(reconnectGraceTimeoutRef.current);
        reconnectGraceTimeoutRef.current = null;
      }
      if (restoredUiTimerRef.current) {
        clearTimeout(restoredUiTimerRef.current);
        restoredUiTimerRef.current = null;
      }
    };

    const stopSocketHeartbeat = () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      heartbeatSocketRef.current = null;
      heartbeatPendingRef.current = null;
    };

    const clearSocketConnectTimeout = () => {
      clearTimeout(socketConnectTimeoutRef.current);
      socketConnectTimeoutRef.current = null;
    };

    const finalizeAfterReconnectTimeout = () => {
      if (
        reconnectTimeoutFinalizingRef.current ||
        interviewEndedRef.current ||
        !isMountedRef.current
      ) {
        return;
      }

      reconnectTimeoutFinalizingRef.current = true;
      interviewEndedRef.current = true;
      stopInterviewAudioForDisconnect("reconnect-expired");
      clearConnectionUiTimers();
      clearSocketConnectTimeout();
      stopSocketHeartbeat();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const socket = socketRef.current;
      socketRef.current = null;
      try {
        socket?.close();
      } catch (error) {
        console.warn("[ConnectionUI] Failed to close expired interview socket", error);
      }

      connectionUiStateRef.current = "failed";
      setConnectionUi({ state: "failed", secondsRemaining: 0 });
      safeSetStatus("Connection failed");

      setTimeout(() => {
        Promise.resolve()
          .then(() => endInterview?.())
          .catch((error) => {
            console.error(
              "[ConnectionUI] Reconnect timeout finalization failed",
              error,
            );
          });
      }, 1500);
    };

    const resetAnswerForPlayback = () => {
      // Replaying the same question must not erase its captured answer.
      if (recoveredAnswerTurnRef.current === activeTurnIdRef.current &&
          activeTurnIdRef.current) return;
      bufferRef.current = "";
      lastSentInterimRef.current = "";
    };

    const stopInterviewAudioForDisconnect = (reason = "transport") => {
      const wasSpeaking =
        statusRef.current === "AI Speaking..." ||
        isPlayingStreamingRef.current ||
        window.__WAITING_FOR_TTS__ === true;
      const answerText = bufferRef.current.trim();
      const submittedAnswer =
        lastSubmittedAnswerRef.current?.turnId === activeTurnIdRef.current
          ? lastSubmittedAnswerRef.current.text
          : "";
      const savedAnswer = reconnectAnswerRef.current;
      reconnectAnswerRef.current = {
        turnId: activeTurnIdRef.current,
        text: answerText || submittedAnswer ||
          (savedAnswer?.turnId === activeTurnIdRef.current ? savedAnswer.text : ""),
        shouldListen: pendingAnswerRef.current || Boolean(answerText) ||
          (savedAnswer?.turnId === activeTurnIdRef.current && savedAnswer.shouldListen),
      };
      nativeListenAllowedRef.current = false;
      clearWaitingGuard();
      restoreListeningAfterReconnectRef.current =
        wasSpeaking || pendingAnswerRef.current || currentTurnShouldListenRef.current;
      // The old audio/mic callbacks are asynchronous. Explicitly move the UI
      // out of AI Speaking before cancelling them, otherwise the callback that
      // normally ends TTS may never arrive after a network cut.
      mediaGenerationRef.current += 1;
      audioStreamGenerationRef.current += 1;
      activeAudioStreamIdRef.current = null;
      activeAudioTurnIdRef.current = null;
      activeAudioQuestionRef.current = "";
      lastAudioStreamSequenceRef.current = 0;
      stopSharedAIPlayback();
      markAudioPlaybackStarted(false);
      // A received question must remain visible during recovery even if its
      // audio never started. This does not create another question/log entry.
      if (activeQuestionTextRef.current) {
        displayAIQuestion(activeQuestionTextRef.current, activeTurnIdRef.current);
      }
      cancelRecoveryReplay();
      questionQueueRef.current = [];
      chunkBufferRef.current = "";
      canReceiveQuestionRef.current = true;
      safeSetStatus("Reconnecting");
      console.warn("[WS] disconnect audio cleanup", {
        reason,
        socketGeneration: socketGenerationRef.current,
        wasSpeaking,
        microphoneActive: recognizingRef.current || !!window.__MIC_STARTED__,
      });

      console.log("[AUDIO] stopping TTS because network disconnected");
      clearTtsKeepAlive();
      window.__ALLOW_TTS_TO_FINISH__ = false;
      browserTtsPlayingRef.current = false;
      browserTtsQueueRef.current = [];
      try {
        window.speechSynthesis?.cancel();
      } catch (error) {}
      stopTrackedAudioSources();
      try {
        audioRef.current?.stop();
      } catch (error) {}
      audioRef.current = null;

      console.log("[AUDIO] stopping microphone because network disconnected");
      try {
        const recognition = recognitionRef.current;
        recognitionRef.current = null;
        if (recognition) {
          recognition.onstart = recognition.onresult = recognition.onend = recognition.onerror = null;
          recognition.abort();
        }
      } catch (error) {}
      recognizingRef.current = false;
      recognitionStartingRef.current = false;
      window.__MIC_STARTED__ = false;
      // The recorder owns the App microphone through its MediaStreamDestination.
      // A transport outage must not disconnect or stop that source: MediaRecorder
      // continues running while the backend later replays the interrupted turn.
      // Mobile STT still releases this mic through __RELEASE_APP_MIC_FOR_STT__
      // when recognition actually starts.
      stopGeminiAudioCapture();

      console.log("[AUDIO] clearing audio buffers");
      audioQueueRef.current = [];
      geminiPcmQueueRef.current = [];
      geminiActiveBatchesRef.current.clear();
      geminiAudioChunksRef.current = [];
      lastProcessedIndexRef.current = -1;
      nextPlayTimeRef.current = 0;
      geminiPlaybackStartTimeRef.current = 0;
      audioStreamFinalizedRef.current = false;
      isPlayingStreamingRef.current = false;
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
      window.__WAITING_FOR_TTS__ = false;
      window.__TTS_AUDIO_PLAYED__ = false;
      window.__TTS_ON_END__ = null;
      window.__TTS_ON_AUDIO_START__ = null;
      window.__DID_SPEAKING_START_RECEIVED__ = false;
      window.__DID_SPEAKING_END_RECEIVED__ = false;
      if (ttsDoneResolverRef.current) {
        try {
          clearTimeout(ttsDoneResolverRef.current.__timeoutId);
          ttsDoneResolverRef.current();
        } catch (error) {}
        ttsDoneResolverRef.current = null;
      }
      console.log("[STATE] AI_SPEAKING/LISTENING -> RECONNECTING");
    };

    const interruptInterviewConnection = () => {
      if (!isMountedRef.current || interviewEndedRef.current) return;
      reconnectInFlightRef.current = false;
      showConnectionInterrupted();
      safeSetStatus("Reconnecting");
      if (codingHandoffRef.current) return;
      clearSocketConnectTimeout();
      stopSocketHeartbeat();
      const socket = socketRef.current;
      socketRef.current = null;
      stopInterviewAudioForDisconnect("network-interruption");
      if (socket) {
        try {
          socket.close();
        } catch (error) {}
      }
      scheduleReconnect();
    };

    const scheduleReconnect = () => {
      if (
        reconnectTimerRef.current ||
        codingHandoffRef.current ||
        interviewEndedRef.current ||
        !isMountedRef.current ||
        connectionUiStateRef.current === "failed"
      ) {
        return;
      }

      // If the browser is offline, do not create WebSocket
      // connections repeatedly. The `online` event will trigger
      // the reconnect when connectivity returns.
      if (!networkReachableRef.current) {
        console.log(
          "[WebSocket] Browser is offline. Waiting for online event before reconnecting.",
        );
        return;
      }

      const delay = Math.min(1000 * 2 ** reconnectAttemptRef.current, 10000);

      reconnectAttemptRef.current += 1;

      console.log(
        `[WebSocket] Scheduling reconnect attempt #${reconnectAttemptRef.current} in ${delay}ms`,
      );

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;

        if (interviewEndedRef.current || codingHandoffRef.current || !isMountedRef.current) {
          reconnectInFlightRef.current = false;
          return;
        }

        // Double-check before opening a new socket.
        const existingSocket = socketRef.current;

        if (
          existingSocket &&
          (existingSocket.readyState === WebSocket.CONNECTING ||
            existingSocket.readyState === WebSocket.OPEN)
        ) {
          console.log(
            "[WebSocket] Reconnect skipped because a socket is already active/connecting.",
          );
          return;
        }

        // If connectivity disappeared again while waiting,
        // wait for the browser's `online` event.
        if (!networkReachableRef.current) {
          reconnectInFlightRef.current = false;
          console.log(
            "[WebSocket] Connectivity lost before reconnect attempt. Waiting for online event.",
          );
          return;
        }

        if (connectionInterruptedRef.current) {
          reconnectInFlightRef.current = true;
        }
        startInterview();
      }, delay);
    };
    const handleHeartbeatFailure = (socket) => {
      if (socketRef.current !== socket || interviewEndedRef.current) return;
      console.warn(
        "[WebSocket] Heartbeat acknowledgement timed out; using existing reconnect flow",
      );
      interruptInterviewConnection();
    };

    const startSocketHeartbeat = (socket) => {
      if (role === "admin" || codingHandoffRef.current) return;
      stopSocketHeartbeat();
      heartbeatSocketRef.current = socket;
      lastHeartbeatAckRef.current = Date.now();
      lastInterviewSocketTrafficAtRef.current = Date.now();
      heartbeatIntervalRef.current = setInterval(() => {
        if (
          socketRef.current !== socket ||
          heartbeatSocketRef.current !== socket ||
          interviewEndedRef.current ||
          codingHandoffRef.current
        ) {
          stopSocketHeartbeat();
          return;
        }

        const pending = heartbeatPendingRef.current;
        if (pending && Date.now() - pending.sentAt >= HEARTBEAT_TIMEOUT_MS) {
          handleHeartbeatFailure(socket);
          return;
        }

        if (pending) return;

        if (socket.readyState !== WebSocket.OPEN) return;
        try {
          const heartbeatId = `${Date.now()}-${heartbeatSequenceRef.current++}`;
          heartbeatPendingRef.current = { id: heartbeatId, sentAt: Date.now() };
          socket.send(
            JSON.stringify({
              type: "connection_health_check",
              heartbeat_id: heartbeatId,
            }),
          );
        } catch (error) {
          handleHeartbeatFailure(socket);
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    const showConnectionInterrupted = () => {
      if (!isMountedRef.current || interviewEndedRef.current) return;
      if (connectionInterruptedRef.current) return;

      connectionInterruptedRef.current = true;

      if (restoredUiTimerRef.current) {
        clearTimeout(restoredUiTimerRef.current);
        restoredUiTimerRef.current = null;
      }

      connectionUiDeadlineRef.current =
        Date.now() + RECONNECT_GRACE_SECONDS * 1000;
      reconnectGraceTimeoutRef.current = setTimeout(() => {
        reconnectGraceTimeoutRef.current = null;
        if (connectionInterruptedRef.current && !interviewEndedRef.current) {
          finalizeAfterReconnectTimeout();
        }
      }, RECONNECT_GRACE_SECONDS * 1000);

      connectionUiStateRef.current = "reconnecting";

      setConnectionUi({
        state: "reconnecting",
        deadline: connectionUiDeadlineRef.current,
        secondsRemaining: RECONNECT_GRACE_SECONDS,
      });

      if (reconnectUiIntervalRef.current) {
        clearInterval(reconnectUiIntervalRef.current);
      }

      reconnectUiIntervalRef.current = setInterval(() => {
        const remaining = Math.max(
          0,
          Math.ceil((connectionUiDeadlineRef.current - Date.now()) / 1000),
        );

        setConnectionUi((current) =>
          current.state === "reconnecting"
            ? { ...current, secondsRemaining: remaining }
            : current,
        );
      }, 1000);
    };

    const retryConnection = () => {
      if (interviewEndedRef.current || !isMountedRef.current) return;

      // IMPORTANT: after a real network interruption, an existing WebSocket
      // can remain locally OPEN even though its TCP path is dead. Do not treat
      // that stale socket as a restored connection. Force a fresh WebSocket
      // transport; the backend will attach it to the existing interview
      // runtime because the interviewId is unchanged.
      if (
        connectionInterruptedRef.current &&
        !codingHandoffRef.current &&
        networkReachableRef.current
      ) {
        if (reconnectInFlightRef.current) {
          return;
        }
        reconnectInFlightRef.current = true;
        clearSocketConnectTimeout();
        stopSocketHeartbeat();

        const staleSocket = socketRef.current;
        socketRef.current = null;

        if (staleSocket) {
          try {
            staleSocket.close();
          } catch (error) {}
        }

        reconnectAttemptRef.current = 0;
        console.log(
          "[WebSocket] Network restored after interruption. Forcing a fresh socket to resume the existing interview.",
        );
        scheduleReconnect();
        return;
      }

      if (
        !codingHandoffRef.current &&
        socketRef.current?.readyState === WebSocket.OPEN &&
        networkReachableRef.current
      ) {
        startSocketHeartbeat(socketRef.current);
        return;
      }

      if (
        codingHandoffRef.current &&
        networkReachableRef.current
      ) {
        showConnectionRestored();
        safeSetStatus("Ready for coding");
        return;
      }

      // Preserve the original grace deadline across failed retry attempts.
      reconnectAttemptRef.current = 0;
      showConnectionInterrupted();
      scheduleReconnect();
    };

    const showConnectionRestored = ({ fromInterviewSocket = false } = {}) => {
      if (
        !isMountedRef.current ||
        interviewEndedRef.current ||
        (!fromInterviewSocket &&
          (!networkReachableRef.current || navigator.onLine === false))
      ) {
        return;
      }

      // Prevent duplicate "restored" transitions.
      if (
        connectionUiStateRef.current === "connected" &&
        !connectionInterruptedRef.current
      ) {
        return;
      }

      console.log("[ConnectionUI] Connection restored");

      connectionInterruptedRef.current = false;
      reconnectInFlightRef.current = false;
      reconnectAttemptRef.current = 0;

      clearConnectionUiTimers();

      connectionUiStateRef.current = "restored";

      setConnectionUi({
        state: "restored",
        secondsRemaining: 0,
      });

      restoredUiTimerRef.current = setTimeout(() => {
        restoredUiTimerRef.current = null;

        if (!isMountedRef.current || interviewEndedRef.current) {
          return;
        }

        connectionUiStateRef.current = "connected";

        setConnectionUi({
          state: "connected",
          secondsRemaining: 0,
        });

        console.log("[ConnectionUI] Restored popup dismissed");
      }, 1800);
    };

    const handleCompilerHandoff = () => {
      // The voice transport is no longer required once coding is enabled.
      codingHandoffRef.current = true;
      pendingAnswerRef.current = false;
      stopSocketHeartbeat();
      clearSocketConnectTimeout();
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      clearConnectionUiTimers();
      stopGeminiAudioCapture();
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
      try {
        recognitionRef.current?.stop();
      } catch (error) {}
      recognizingRef.current = false;
      if (networkReachableRef.current && navigator.onLine !== false) {
        connectionInterruptedRef.current = false;
        connectionUiStateRef.current = "connected";
        setConnectionUi({ state: "connected", secondsRemaining: 0 });
        safeSetStatus("Ready for coding");
      }
    };

    useEffect(() => {
      if (compilerEnabled) networkFnsRef.current.handleCompilerHandoff();
    }, [compilerEnabled]);

    useEffect(
      () => () => {
        clearConnectionUiTimers();
        stopSocketHeartbeat();
        clearSocketConnectTimeout();
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      },
      [],
    );

    const networkFnsRef = useRef({
      showConnectionInterrupted,
      safeSetStatus,
      scheduleReconnect,
      interruptInterviewConnection,
      retryConnection,
      startSocketHeartbeat,
      handleCompilerHandoff,
    });
    useEffect(() => {
      networkFnsRef.current = {
        showConnectionInterrupted,
        safeSetStatus,
        scheduleReconnect,
        interruptInterviewConnection,
        retryConnection,
        startSocketHeartbeat,
        handleCompilerHandoff,
      };
    });

    useEffect(() => {
      if (role === "admin" || interviewEnded) return;
      const handleOffline = () => {
        if (interviewEndedRef.current) return;

        networkReachableRef.current = false;
        console.log("[Network] Candidate internet connection unavailable");
        networkFnsRef.current.interruptInterviewConnection();
      };

      const handleOnline = () => {
        if (interviewEndedRef.current) return;

        console.log("[Network] Browser went online");
        if (codingHandoffRef.current) {
          networkFnsRef.current.retryConnection();
          return;
        }
        if (connectionInterruptedRef.current) {
          networkFnsRef.current.retryConnection();
          return;
        }

        const socket = socketRef.current;

        if (socket && socket.readyState === WebSocket.OPEN) {
          if (heartbeatSocketRef.current === socket) {
            return;
          }

          console.log(
            "[Network] Existing WebSocket is already OPEN after network recovery",
          );

          networkFnsRef.current.startSocketHeartbeat(socket);
          return;
        }

        console.log("[Network] WebSocket is not OPEN. Scheduling reconnect.");

        networkFnsRef.current.scheduleReconnect();
      };

      const handleConnectivityChange = (reachable, { reason } = {}) => {
        const socket = socketRef.current;
        const hasFreshInterviewHeartbeat =
          socket?.readyState === WebSocket.OPEN &&
          heartbeatSocketRef.current === socket &&
          Date.now() - lastHeartbeatAckRef.current < HEARTBEAT_TIMEOUT_MS;

        if (!reachable && reason === "probe_failure") {
          // The public gstatic probe is advisory only. Corporate firewalls,
          // DNS filtering, or a blocked third-party origin can fail it while
          // our interview WebSocket is demonstrably healthy. Do not tear down
          // a live interview transport in that case; its acknowledged
          // application heartbeat is the authoritative signal.
          if (hasFreshInterviewHeartbeat) {
            networkReachableRef.current = true;
            console.warn(
              "[Network] Ignoring failed external connectivity probe; interview heartbeat is healthy.",
            );
            return;
          }
          if (
            connectionInterruptedRef.current ||
            reconnectInFlightRef.current
          ) {
            networkReachableRef.current = false;
            console.log(
              "[Network] Ignoring a failed internet probe while the interview is already reconnecting.",
            );
            return;
          }
        }

        if (
          reachable &&
          reason === "probe_success" &&
          !connectionInterruptedRef.current &&
          hasFreshInterviewHeartbeat
        ) {
          networkReachableRef.current = true;
          return;
        }

        networkReachableRef.current = reachable;
        if (reachable) handleOnline();
        else handleOffline();
      };

      return monitorInterviewConnectivity({
        probeInternet: usesGeminiLiveProvider,
        probeUrl: INTERNET_CONNECTIVITY_PROBE_URL,
        initialReachable: networkReachableRef.current,
        onChange: handleConnectivityChange,
      });
    }, [role, interviewEnded, compilerEnabled, usesGeminiLiveProvider]);

    const dispatchBrowserTtsEvent = (eventName, completedTurnId = null) => {
      const gender = avatarGenderRef.current === "female" ? "female" : "male";
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { gender, avatar_gender: gender },
        }),
      );
      if (eventName === "browser_tts_end" && completedTurnId === activeTurnIdRef.current) clearWaitingGuard();
      return eventName === "browser_tts_end" && finishClosingPlayback(completedTurnId);
    };

    const fetchInterviewProvider = async () => {
      try {
        const response = await fetch(`${API_BASE}/settings/interview-provider`);
        if (!response.ok) {
          throw new Error(
            `Interview provider fetch failed: ${response.status}`,
          );
        }
        const data = await response.json();
        isGeminiLiveRef.current = data?.provider === "gemini_live";
        console.log(
          "🎤 Interview provider:",
          data?.provider,
          "geminiLive:",
          isGeminiLiveRef.current,
        );
      } catch (error) {
        console.warn(
          "🎤 Could not fetch interview provider, defaulting to legacy:",
          error,
        );
        isGeminiLiveRef.current = false;
      }
    };

    const convertFloat32ToInt16 = (buffer) => {
      const output = new Int16Array(buffer.length);
      for (let i = 0; i < buffer.length; i += 1) {
        const s = Math.max(-1, Math.min(1, buffer[i]));
        output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      return output;
    };

    const decodeGeminiBase64Chunk = (base64String) => {
      if (!base64String) return new Int16Array(0);
      try {
        const binary = atob(base64String);
        const buffer = new ArrayBuffer(binary.length);
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }
        return new Int16Array(buffer);
      } catch (error) {
        console.error("❌ Failed to decode Gemini audio chunk base64:", error);
        return new Int16Array(0);
      }
    };

    const downsampleBuffer = (buffer, inputRate, outputRate) => {
      if (outputRate === inputRate) return buffer;
      const sampleRateRatio = inputRate / outputRate;
      const newLength = Math.round(buffer.length / sampleRateRatio);
      const result = new Float32Array(newLength);
      let offsetResult = 0;
      let offsetBuffer = 0;
      while (offsetResult < newLength) {
        const nextOffsetBuffer = Math.round(
          (offsetResult + 1) * sampleRateRatio,
        );
        let accum = 0;
        let count = 0;
        for (
          let i = offsetBuffer;
          i < nextOffsetBuffer && i < buffer.length;
          i += 1
        ) {
          accum += buffer[i];
          count += 1;
        }
        result[offsetResult] = count > 0 ? accum / count : 0;
        offsetResult += 1;
        offsetBuffer = nextOffsetBuffer;
      }
      return result;
    };

    const flushGeminiAudioBuffer = () => {
      const chunkCount = geminiAudioChunksRef.current.length;
      if (!chunkCount) return;

      console.log(
        `[GEMINI DIAGNOSTIC] flushGeminiAudioBuffer called: chunkCount=${chunkCount}, geminiSpeechFrames=${geminiSpeechFramesRef.current}, pendingAnswer=${pendingAnswerRef.current}, WAITING_FOR_TTS=${window.__WAITING_FOR_TTS__}, isPlaying=${isPlayingStreamingRef.current}`,
      );

      if (geminiSpeechFramesRef.current < GEMINI_MIN_SPEECH_FRAMES) {
        console.log(
          `🎤 [GEMINI] Dropping short noise packet (${geminiSpeechFramesRef.current} frames)`,
        );
        geminiAudioChunksRef.current = [];
        geminiSpeechFramesRef.current = 0;
        geminiSpeechActiveRef.current = false;
        return;
      }

      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        geminiAudioChunksRef.current = [];
        geminiSpeechFramesRef.current = 0;
        geminiSpeechActiveRef.current = false;
        return;
      }

      const totalLength = geminiAudioChunksRef.current.reduce(
        (sum, chunk) => sum + chunk.length,
        0,
      );
      const combined = new Int16Array(totalLength);
      let offset = 0;
      for (const chunk of geminiAudioChunksRef.current) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }
      geminiAudioChunksRef.current = [];
      geminiSpeechFramesRef.current = 0;
      geminiSpeechActiveRef.current = false;

      try {
        console.log(
          `[GEMINI DIAGNOSTIC] Sending combined PCM: samples=${combined.length}, bytes=${combined.byteLength}, socketState=${socketRef.current?.readyState}`,
        );
        socketRef.current.send(combined.buffer);
        console.log(
          `🎤 [GEMINI] Sent ${combined.length} PCM samples (${combined.byteLength} bytes)`,
        );
      } catch (e) {
        console.error("❌ Failed to send Gemini audio bytes:", e);
      }
    };

    const stopGeminiAudioCapture = () => {
      nativeCaptureRef.current?.stop();
      nativeCaptureRef.current = null;
      nativeCaptureReadyRef.current = false;
      nativeListenAllowedRef.current = false;
      nativePlaybackAckRef.current = null;
      if (geminiSpeechEndTimeoutRef.current) {
        clearTimeout(geminiSpeechEndTimeoutRef.current);
        geminiSpeechEndTimeoutRef.current = null;
      }
      if (micProcessorRef.current) {
        try {
          micProcessorRef.current.disconnect();
        } catch (e) {}
        micProcessorRef.current = null;
      }
      if (micSourceRef.current) {
        try {
          micSourceRef.current.disconnect();
        } catch (e) {}
        micSourceRef.current = null;
      }
      if (micStreamRef.current) {
        try {
          micStreamRef.current.getTracks().forEach((track) => track.stop());
        } catch (e) {}
        micStreamRef.current = null;
      }
      geminiSpeechActiveRef.current = false;
      geminiAudioChunksRef.current = [];
    };

    const handleGeminiAudioProcess = (event) => {
      console.log(
        "🎤 [GEMINI] Binary mic capture is disabled; browser recognition handles candidate answers.",
      );
      return;
    };

    const startGeminiAudioCapture = async () => {
      console.log(
        "🎤 [GEMINI] Browser SpeechRecognition handles candidate answers; Gemini mic capture is disabled.",
      );
    };

    // ── Gemini Live audio processing: PCM16 24kHz to Float32 playback ───────
    const geminiTtsStartedRef = useRef(false); // Track if browser_tts_start has been dispatched
    const geminiSpeechDetectedRef = useRef(false);
    const geminiSpeechFramesRef = useRef(0);
    const GEMINI_MIN_SPEECH_FRAMES = 3; // ~300ms of detected speech before sending

    const isCurrentGeminiAudioFrame = (data, isStart = false) => {
      // Leave other providers and avatar-owned playback on their existing paths.
      if (data.provider !== "gemini" ||
          avatarModeRef.current === "custom" || avatarModeRef.current === "did") return true;
      const turnId = data.turn_id || null;
      if (turnId !== (activeTurnIdRef.current || null)) return false;
      if (!data.stream_id) return false;
      if (isStart) {
        if (!nativeModeRef.current && (typeof data.question !== "string" || !data.question.trim())) return false;
        if (data.stream_id === activeAudioStreamIdRef.current) return false;
        if (Number.isFinite(data.stream_sequence) &&
            data.stream_sequence <= lastAudioStreamSequenceRef.current) return false;
        return true;
      }
      return data.stream_id === activeAudioStreamIdRef.current &&
        turnId === activeAudioTurnIdRef.current;
    };

    const processGeminiAudioBatch = async () => {
      const mediaGeneration = mediaGenerationRef.current;
      const streamGeneration = audioStreamGenerationRef.current;
      const mediaTurnId = activeAudioTurnIdRef.current;
      const mediaQuestion = activeAudioQuestionRef.current;
      if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current || mediaTurnId !== (activeTurnIdRef.current || null))) return;
      const audioCtx = audioContextRef.current;
      if (!audioCtx) {
        console.error("❌ Audio context not available");
        return;
      }

      if (audioCtx.state === "suspended") {
        try {
          await audioCtx.resume();
        } catch (e) {
          console.error("🔊 Failed to resume audio context:", e);
        }
      }

      if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current || mediaTurnId !== (activeTurnIdRef.current || null))) return;
      // 📋 DIAGNOSTIC LOG
      console.log(
        `[GEMINI DIAGNOSTIC] processGeminiAudioBatch called: queue=${geminiPcmQueueRef.current.length}, finalized=${audioStreamFinalizedRef.current}, playing=${isPlayingStreamingRef.current}`,
      );

      // Only process if we have enough chunks OR stream is finalized.
      // For Gemini live playback, start as soon as there is at least one chunk
      // and the stream isn't already playing; otherwise wait for larger batches.
      const requiredChunks = isPlayingStreamingRef.current
        ? (nativeModeRef.current ? 1 : GEMINI_BATCH_SIZE)
        : 1;
      if (
        geminiPcmQueueRef.current.length < requiredChunks &&
        !audioStreamFinalizedRef.current
      ) {
        console.log(
          `🎵 [GEMINI] Waiting for more chunks (have ${geminiPcmQueueRef.current.length}, need ${requiredChunks})`,
        );
        return;
      }

      // How many chunks to take this batch? If finalized, take everything left!
      const batchChunks = [];
      const takeCount = audioStreamFinalizedRef.current
        ? geminiPcmQueueRef.current.length
        : Math.min(nativeModeRef.current ? 1 : GEMINI_BATCH_SIZE, geminiPcmQueueRef.current.length);
      console.log(
        `[GEMINI DIAGNOSTIC] Taking ${takeCount} chunks for batch (finalized=${audioStreamFinalizedRef.current})`,
      );
      for (let i = 0; i < takeCount; i++) {
        batchChunks.push(geminiPcmQueueRef.current.shift());
      }

      if (batchChunks.length === 0) {
        console.log("🎵 No more Gemini chunks to play");

        if (isPlayingStreamingRef.current) {
          console.log(
            "🎵 Gemini audio still playing, waiting for current batch to finish before finalizing",
          );
          return;
        }

        if (audioStreamFinalizedRef.current) {
          console.log(
            `[GEMINI DIAGNOSTIC] Queue drained, dispatching browser_tts_end`,
          );
          if (geminiTtsStartedRef.current) {
            if (dispatchBrowserTtsEvent("browser_tts_end", mediaTurnId)) return;
          }
          if (ttsSafetyTimeoutRef.current)
            clearTimeout(ttsSafetyTimeoutRef.current);
          ttsSafetyTimeoutRef.current = setTimeout(() => {
            if (!isPlayingStreamingRef.current) {
              try {
                if (ttsDoneResolverRef.current) {
                  clearTimeout(ttsDoneResolverRef.current.__timeoutId);
                  ttsDoneResolverRef.current();
                  ttsDoneResolverRef.current = null;
                }
              } catch (e) {}
              window.__WAITING_FOR_TTS__ = false;
              window.__TTS_AUDIO_PLAYED__ = false;
              try {
                window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
              } catch (e) {}
              console.log(
                `[GEMINI DIAGNOSTIC] Avatar mode changed to Listening`,
              );
              safeSetStatus("Listening");
              markGeminiAnswerReady();
              scheduleRecognitionStart();
            }
            ttsSafetyTimeoutRef.current = null;
          }, 1000);
        }
        return;
      }

      const concatenated = batchChunks.reduce((acc, chunk) => {
        if (!chunk) return acc;

        if (chunk instanceof Int16Array) {
          const merged = new Int16Array(acc.length + chunk.length);
          merged.set(acc, 0);
          merged.set(chunk, acc.length);
          return merged;
        }

        if (chunk instanceof Uint8Array) {
          const view = new Int16Array(chunk.buffer);
          const merged = new Int16Array(acc.length + view.length);
          merged.set(acc, 0);
          merged.set(view, acc.length);
          return merged;
        }

        if (Array.isArray(chunk)) {
          const merged = new Int16Array(acc.length + chunk.length);
          merged.set(acc, 0);
          merged.set(chunk, acc.length);
          return merged;
        }

        return acc;
      }, new Int16Array(0));

      if (concatenated.length === 0) {
        console.log("🎵 Gemini batch had no PCM data after concatenation");
        return;
      }

      const float32Array = new Float32Array(concatenated.length);
      for (let i = 0; i < concatenated.length; i++) {
        float32Array[i] = concatenated[i] / 32768.0;
      }

      const audioBuffer = audioCtx.createBuffer(1, float32Array.length, 24000);
      audioBuffer.getChannelData(0).set(float32Array);

      const startTime = Math.max(
        geminiPlaybackStartTimeRef.current,
        audioCtx.currentTime,
      );
      console.log(
        `🎵 [GEMINI PLAY START] Starting ${batchChunks.length}-chunk batch at ${startTime}s (current: ${audioCtx.currentTime}s)`,
      );
      geminiPlaybackStartTimeRef.current = startTime + audioBuffer.duration;
      const batchToken = {};
      geminiActiveBatchesRef.current.add(batchToken);
      isPlayingStreamingRef.current = true;

      let source;
      try {
        source = await playBufferThroughSharedDestination({
        audioBuffer,
        localPlayback: true,
          isPlaybackValid: () => isMediaGenerationCurrent(mediaGeneration) &&
            streamGeneration === audioStreamGenerationRef.current &&
            mediaTurnId === (activeTurnIdRef.current || null),
        when: startTime,

        onStarted: () => {
            if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current || mediaTurnId !== (activeTurnIdRef.current || null))) return;
            if (nativeModeRef.current && !audioPlaybackStartedRef.current) {
              console.info("[PROVIDER_AUDIT] stage=browser_playback_started provider=gemini_live_native_audio", { turn: mediaTurnId, stream: activeAudioStreamIdRef.current, sampleRate: 24000 });
              if (socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify({
                  type: "native_playback_started",
                  turn_id: mediaTurnId,
                  stream_id: activeAudioStreamIdRef.current,
                  stream_sequence: lastAudioStreamSequenceRef.current,
                }));
              }
            }
            markAudioPlaybackStarted(true);
            safeSetStatus("AI Speaking...");
          if (mediaQuestion || (nativeModeRef.current && activeQuestionTextRef.current)) {
            console.log(
              "🎤 [GEMINI SYNC] TTS started → displaying question:",
              mediaQuestion.substring(0, 120),
            );

            displayAIQuestion(nativeModeRef.current ? activeQuestionTextRef.current : mediaQuestion, mediaTurnId);
          }
        },

        onEnded: () => {
            if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current || mediaTurnId !== (activeTurnIdRef.current || null))) return;
          geminiActiveBatchesRef.current.delete(batchToken);
          isPlayingStreamingRef.current = geminiActiveBatchesRef.current.size > 0;
          if (isPlayingStreamingRef.current) return;
          if (!audioStreamFinalizedRef.current && geminiPcmQueueRef.current.length === 0) {
            markAudioPlaybackStarted(false);
            armWaitingGuard(mediaTurnId);
          }
          console.log(
            `🎵 [GEMINI BATCH ENDED] ${batchChunks.length}-chunk batch playback finished, remaining queue=${geminiPcmQueueRef.current.length}, finalized=${audioStreamFinalizedRef.current}`,
          );
          console.log(
            `[GEMINI DIAGNOSTIC] Playback queue size: ${geminiPcmQueueRef.current.length}`,
          );

          if (
            audioStreamFinalizedRef.current &&
            geminiPcmQueueRef.current.length === 0
          ) {
            markAudioPlaybackStarted(false);
            console.log("🎤 ✅ ALL GEMINI AUDIO PLAYBACK COMPLETED");
            console.log(
              `[GEMINI DIAGNOSTIC] Queue drained, dispatching browser_tts_end`,
            );
            if (dispatchBrowserTtsEvent("browser_tts_end", mediaTurnId)) return;

            if (ttsDoneResolverRef.current) {
              try {
                clearTimeout(ttsDoneResolverRef.current.__timeoutId);
              } catch (e) {}
              try {
                ttsDoneResolverRef.current();
              } catch (e) {}
              ttsDoneResolverRef.current = null;
            }

            geminiPlaybackStartTimeRef.current = 0;
            window.__TTS_AUDIO_PLAYED__ = false;
            try {
              window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
            } catch (e) {}
            window.__WAITING_FOR_TTS__ = false;
            isPlayingStreamingRef.current = false;
            audioStreamFinalizedRef.current = false;
            resetAnswerForPlayback();
            if (currentTurnShouldListenRef.current) {
              console.log(
                `[GEMINI DIAGNOSTIC] Avatar mode changed to Listening`,
              );
              safeSetStatus("Listening");
              markGeminiAnswerReady();
              const onEndCallback = window.__TTS_ON_END__;
              window.__TTS_ON_END__ = null;
              if (onEndCallback) {
                console.log("🎤 Calling onEnd callback to start listening");
                try {
                  onEndCallback();
                } catch (e) {
                  console.error("🎤 Error in onEnd callback:", e);
                }
              }
              scheduleRecognitionStart();
            } else {
              safeSetStatus("AI Speaking...");
              canReceiveQuestionRef.current = true;
              drainQueuedQuestions();
            }
          } else {
            console.log(
              `🎵 Processing next Gemini batch... (remaining chunks: ${geminiPcmQueueRef.current.length})`,
            );
            processNextAudioBatch();
          }
        },
        onError: () => {
            if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current || mediaTurnId !== (activeTurnIdRef.current || null))) return;
            markAudioPlaybackStarted(false);
          console.error("❌ Gemini audio source error");
          isPlayingStreamingRef.current = false;
          showInterviewFlowError({code: "AUDIO_PLAYBACK_FAILED", turn_id: mediaTurnId, retryable: true,
            message: "The question audio could not be played. Please retry audio."});
        },
      });
      } catch (error) {
        geminiActiveBatchesRef.current.delete(batchToken);
        showInterviewFlowError({code: "AUDIO_PLAYBACK_FAILED", turn_id: mediaTurnId, retryable: true,
          message: "The question audio could not be played. Please retry audio."});
        return;
      }
      if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current || mediaTurnId !== (activeTurnIdRef.current || null))) {
        try { source?.stop(); } catch (e) {}
        return;
      }
      trackAudioSource(source);

      if (!source) {
        geminiActiveBatchesRef.current.delete(batchToken);
        showInterviewFlowError({code: "AUDIO_PLAYBACK_FAILED", turn_id: mediaTurnId, retryable: true,
          message: "The question audio could not be played. Please retry audio."});
        return;
      }
      if (ttsSafetyTimeoutRef.current) {
        clearTimeout(ttsSafetyTimeoutRef.current);
        ttsSafetyTimeoutRef.current = null;
      }

      if (recognitionRef.current && recognizingRef.current) {
        try {
          console.log("🎤 Stopping recognition during AI speech playback");
          recognitionRef.current.stop();
          recognizingRef.current = false;
        } catch (e) {
          console.warn("🎤 Error stopping recognition:", e);
        }
      }

      window.__TTS_AUDIO_PLAYED__ = true;
      try {
        window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
      } catch (e) {}
      if (window.__TTS_ON_AUDIO_START__) window.__TTS_ON_AUDIO_START__();
      safeSetStatus("AI Speaking...");

      // Dispatch browser_tts_start once when first audio chunk starts playing
      if (!geminiTtsStartedRef.current) {
        console.log(
          `[GEMINI DIAGNOSTIC] New Gemini response started, dispatching browser_tts_start`,
        );
        dispatchBrowserTtsEvent("browser_tts_start");
        geminiTtsStartedRef.current = true;
      }
    };

    // ── Called when actual audio silence is detected on the D-ID stream ────
    // This is the single place that transitions VoiceInterview from AI-speaking
    // to Listening in D-ID mode. It fires ONLY after real audio has gone quiet.
    const handleDIDSpeakingFinished = () => {
      if (isInterviewTransportInterrupted() || interviewEndedRef.current) return;
      if (!isMountedRef.current) return;
      if (!window.__DID_SPEAKING_END_RECEIVED__) {
        // avatar_speaking_end not yet received — audio paused mid-stream, ignore
        console.log(
          "🔇 [DID] Silence detected but no speaking_end yet — ignoring",
        );
        return;
      }
      console.log(
        "🎬 [VoiceInterview] D-ID audio truly finished — transitioning to Listening",
      );
      window.__DID_SPEAKING_START_RECEIVED__ = false;
      window.__DID_SPEAKING_END_RECEIVED__ = false;
      if (didSpeakingEndFallbackRef.current) {
        clearTimeout(didSpeakingEndFallbackRef.current);
        didSpeakingEndFallbackRef.current = null;
      }
      window.__WAITING_FOR_TTS__ = false;
      window.__TTS_AUDIO_PLAYED__ = true;
      if (ttsDoneResolverRef.current) {
        try {
          clearTimeout(ttsDoneResolverRef.current.__timeoutId);
        } catch (e) {}
        const resolver = ttsDoneResolverRef.current;
        ttsDoneResolverRef.current = null;
        resolver();
      }
      audioQueueRef.current = [];
      lastProcessedIndexRef.current = -1;
      nextPlayTimeRef.current = 0;
      isPlayingStreamingRef.current = false;
      audioStreamFinalizedRef.current = false;
      window.__TTS_AUDIO_PLAYED__ = false;
      if (currentTurnShouldListenRef.current) {
        safeSetStatus("Listening");
        markGeminiAnswerReady();
        // Fix 7: scheduleRecognitionStart handles mic release + delay + start
        scheduleRecognitionStart();
      } else {
        safeSetStatus("AI Speaking...");
        canReceiveQuestionRef.current = true;
        drainQueuedQuestions();
      }
    };

    const SIGNALING_TYPES = new Set([
      "init_avatar_stream",
      "webrtc_offer",
      "avatar_video",
      "avatar_static",
      "avatar_speaking",
      "avatar_speaking_start",
      "avatar_speaking_end",
      "avatar_segment",
      "audio_stream_start",
      "audio_start",
      "audio_chunk",
      "audio_complete",
      "corrected_transcript",
      "ice_candidate",
      "sdp_answer",
      "audio_end",
    ]);

    const isSignalingMessage = (msg) => {
      try {
        const data = JSON.parse(msg);
        if (SIGNALING_TYPES.has(data.type)) return true;
        if (data.category === "avatar_signaling") return true;
        return false;
      } catch (e) {
        return false;
      }
    };

    const processNextAudioBatch = async () => {
      const mediaGeneration = mediaGenerationRef.current;
      const streamGeneration = audioStreamGenerationRef.current;
      const mediaTurnId = activeTurnIdRef.current;
      if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current)) return;
      if (currentAudioProviderRef.current === "gemini") {
        return processGeminiAudioBatch();
      }

      const audioCtx = audioContextRef.current;
      if (!audioCtx) {
        console.error("❌ Audio context not available");
        return;
      }

      if (audioCtx.state === "suspended") {
        try {
          await audioCtx.resume();
        } catch (e) {
          console.error("🔊 Failed to resume audio context:", e);
        }
      }

      if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current)) return;
      const unplayedChunks =
        audioQueueRef.current.length - lastProcessedIndexRef.current - 1;

      console.log(
        `🎵 [PROCESS BATCH] Unplayed: ${unplayedChunks}, Queue size: ${audioQueueRef.current.length}, Last index: ${lastProcessedIndexRef.current}`,
      );

      if (unplayedChunks <= 0) {
        console.log("🎵 No more chunks to play");
        isPlayingStreamingRef.current = false;
        if (!window.__TTS_AUDIO_PLAYED__) {
          console.log("🎵 TTS never played, returning");
          return;
        }
        if (ttsSafetyTimeoutRef.current)
          clearTimeout(ttsSafetyTimeoutRef.current);
        ttsSafetyTimeoutRef.current = setTimeout(() => {
          if (!isPlayingStreamingRef.current) {
            try {
              if (ttsDoneResolverRef.current) {
                clearTimeout(ttsDoneResolverRef.current.__timeoutId);
                ttsDoneResolverRef.current();
                ttsDoneResolverRef.current = null;
              }
            } catch (e) {}
            if (dispatchBrowserTtsEvent("browser_tts_end", mediaTurnId)) return;
            window.__WAITING_FOR_TTS__ = false;
            window.__TTS_AUDIO_PLAYED__ = false;
            try {
              window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
            } catch (e) {}
            if (currentTurnShouldListenRef.current) {
              safeSetStatus("Listening");
              markGeminiAnswerReady();
              // Fix 7: scheduleRecognitionStart handles mic release + delay + start
              scheduleRecognitionStart();
            } else {
              safeSetStatus("AI Speaking...");
              canReceiveQuestionRef.current = true;
              drainQueuedQuestions();
            }
          }
          ttsSafetyTimeoutRef.current = null;
        }, 400);
        return;
      }

      isPlayingStreamingRef.current = true;

      try {
        const batchSize = Math.min(10, unplayedChunks);
        const startIdx = lastProcessedIndexRef.current + 1;
        const endIdx = Math.min(
          startIdx + batchSize,
          audioQueueRef.current.length,
        );
        const batch = audioQueueRef.current.slice(startIdx, endIdx);

        console.log(
          `🎵 [DECODE BATCH] Processing batch: ${startIdx} to ${endIdx} (size: ${batchSize})`,
        );

        // ✅ FIX: Convert base64 to binary
        let totalByteLength = 0;
        const binaryStrings = [];

        for (const chunk of batch) {
          try {
            const binaryStr = atob(chunk.data);
            binaryStrings.push(binaryStr);
            totalByteLength += binaryStr.length;
          } catch (e) {
            console.error(`❌ Failed to decode chunk:`, e, chunk);
            throw e;
          }
        }

        console.log(`🎵 [DECODE] Total bytes to decode: ${totalByteLength}`);

        const combined = new Uint8Array(totalByteLength);
        let offset = 0;

        for (const binaryStr of binaryStrings) {
          for (let i = 0; i < binaryStr.length; i++) {
            combined[offset++] = binaryStr.charCodeAt(i);
          }
        }

        lastProcessedIndexRef.current = endIdx - 1;

        console.log(
          `🎵 [DECODE AUDIO] Starting decodeAudioData with ${combined.byteLength} bytes`,
        );
        const decodedBuffer = await audioCtx.decodeAudioData(combined.buffer);
        console.log(
          `✅ [DECODE SUCCESS] Duration: ${decodedBuffer.duration}s, Channels: ${decodedBuffer.numberOfChannels}`,
        );

        if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current)) return;
        const startTime = Math.max(
          nextPlayTimeRef.current,
          audioCtx.currentTime,
        );
        console.log(
          `🎵 [PLAY START] Starting playback at ${startTime}s (current: ${audioCtx.currentTime}s)`,
        );
        nextPlayTimeRef.current = startTime + decodedBuffer.duration;
        const source = await playBufferThroughSharedDestination({
          audioBuffer: decodedBuffer,
          localPlayback: true,
          isPlaybackValid: () => isMediaGenerationCurrent(mediaGeneration) &&
            streamGeneration === audioStreamGenerationRef.current,
          when: startTime,

          onStarted: () => {
            if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current)) return;
            markAudioPlaybackStarted(true);
            safeSetStatus("AI Speaking...");
            if (pendingQuestionRef.current) {
              console.log(
                "🎤 [OPENAI SYNC] TTS started → displaying question:",
                pendingQuestionRef.current.substring(0, 120),
              );

              displayAIQuestion(pendingQuestionRef.current, mediaTurnId);
            }
          },

          onEnded: () => {
            if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current)) return;
            console.log(`🎵 [BATCH ENDED] Batch playback finished`);

            if (
              audioStreamFinalizedRef.current &&
              audioQueueRef.current.length === lastProcessedIndexRef.current + 1
            ) {
            markAudioPlaybackStarted(false);
              console.log("🎤 ✅ ALL AUDIO PLAYBACK COMPLETED");

              if (ttsDoneResolverRef.current) {
                try {
                  clearTimeout(ttsDoneResolverRef.current.__timeoutId);
                } catch (e) {}
                try {
                  ttsDoneResolverRef.current();
                } catch (e) {}
                ttsDoneResolverRef.current = null;
              }

              audioQueueRef.current = [];
              lastProcessedIndexRef.current = -1;
              nextPlayTimeRef.current = 0;
              window.__TTS_AUDIO_PLAYED__ = false;
              try {
                window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
              } catch (e) {}
              window.__WAITING_FOR_TTS__ = false;
              isPlayingStreamingRef.current = false;
              audioStreamFinalizedRef.current = false;

              resetAnswerForPlayback();
              if (dispatchBrowserTtsEvent("browser_tts_end", mediaTurnId)) return;
              if (currentTurnShouldListenRef.current) {
                safeSetStatus("Listening");
                markGeminiAnswerReady();

                const onEndCallback = window.__TTS_ON_END__;
                window.__TTS_ON_END__ = null;

                if (onEndCallback) {
                  console.log("🎤 Calling onEnd callback to start listening");
                  try {
                    onEndCallback();
                  } catch (e) {
                    console.error("🎤 Error in onEnd callback:", e);
                  }
                }

                scheduleRecognitionStart();
              } else {
                safeSetStatus("AI Speaking...");
                canReceiveQuestionRef.current = true;
                drainQueuedQuestions();
              }
            } else {
              console.log(`🎵 Processing next batch...`);
              processNextAudioBatch();
            }
          },
          onError: () => {
            if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current)) return;
            markAudioPlaybackStarted(false);
            console.error("❌ Audio source error");
            isPlayingStreamingRef.current = false;
          },
        });
        if ((!isMediaGenerationCurrent(mediaGeneration) || streamGeneration !== audioStreamGenerationRef.current)) {
          try { source?.stop(); } catch (e) {}
          return;
        }
        trackAudioSource(source);

        if (!source) {
          isPlayingStreamingRef.current = false;
          return;
        }
        if (ttsSafetyTimeoutRef.current) {
          clearTimeout(ttsSafetyTimeoutRef.current);
          ttsSafetyTimeoutRef.current = null;
        }

        if (recognitionRef.current && recognizingRef.current) {
          try {
            console.log("🎤 Stopping recognition during AI speech playback");
            recognitionRef.current.stop();
            recognizingRef.current = false;
          } catch (e) {
            console.warn("🎤 Error stopping recognition:", e);
          }
        }

        window.__TTS_AUDIO_PLAYED__ = true;
        try {
          window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
        } catch (e) {}

        if (window.__TTS_ON_AUDIO_START__) {
          window.__TTS_ON_AUDIO_START__();
        }

        dispatchBrowserTtsEvent("browser_tts_start");
        safeSetStatus("AI Speaking...");
      } catch (err) {
        console.error("❌ Streaming playback error:", err);
        isPlayingStreamingRef.current = false;
      }
    };

    useEffect(() => {
      isPausedRef.current = isPaused;

      if (isPaused) {
        clearTtsKeepAlive();
        try {
          window.speechSynthesis?.cancel();
        } catch (e) {}

        if (audioRef.current) {
          try {
            audioRef.current.stop();
          } catch (e) {}
          audioRef.current = null;
        }

        if (recognitionRef.current && recognizingRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (e) {}
        }
        recognizingRef.current = false;

        pendingCommandRef.current = false;
        pendingAnswerRef.current = false;
      }
    }, [isPaused]);

    useEffect(() => {
      if (onMessagesChange) {
        onMessagesChange(messages);
      }
    }, [messages, onMessagesChange]);

    const appendMessage = (msg) => {
      if (isMountedRef.current) {
        setMessages((prev) => [...prev, msg]);
      }
    };
    const displayAIQuestion = (question, turnId = activeTurnIdRef.current) => {
      if (!isMountedRef.current) return;
      const normalizedQuestion = (question || "").trim();
      if (!normalizedQuestion) return;

      if (turnId && displayedTurnIdsRef.current.has(turnId)) {
        // The provider may speak "greeting + question" while interview_turn
        // contains just the question. Enrich that same row, never append it.
        const comparable = (text) => text.replace(/\s+/g, " ").trim();
        setMessages((prev) => {
          const index = prev.findIndex((message) => message.aiTurnId === turnId);
          if (index < 0) return prev;
          const previousText = prev[index].question.slice("👤 AI: ".length);
          if (comparable(normalizedQuestion) === comparable(previousText) ||
              !comparable(normalizedQuestion).includes(comparable(previousText))) {
            return prev;
          }
          const updated = [...prev];
          updated[index] = { ...prev[index], question: "👤 AI: " + normalizedQuestion };
          return updated;
        });
        questionDisplayedRef.current = true;
        pendingQuestionRef.current = "";
        return;
      }

      // Legacy greeting/control text has no turn ID; retain text deduplication.
      if (!turnId && normalizedQuestion === lastDisplayedQuestionRef.current) return;
      if (turnId) displayedTurnIdsRef.current.add(turnId);
      setMessages((prev) => [
        ...prev,
        { question: "👤 AI: " + normalizedQuestion, ...(turnId ? { aiTurnId: turnId } : {}) },
      ]);
      lastDisplayedQuestionRef.current = normalizedQuestion;
      questionDisplayedRef.current = true;
      pendingQuestionRef.current = "";
    };

    const displayClosingTurn = (data) => {
      if (data?.type !== "interview_turn" || data.message_kind !== "closing") return false;
      if (data.question && data.turn_id) displayAIQuestion(data.question, data.turn_id);
      safeSetStatus("End Interview");
      return true;
    };

    const cancelRecoveryReplay = (question = null) => {
      const recovery = recoveryTurnRef.current;
      if (!recovery || (question && recovery.question !== question)) return;
      if (recoveryReplayTimerRef.current) {
        clearTimeout(recoveryReplayTimerRef.current);
        recoveryReplayTimerRef.current = null;
      }
      recoveryTurnRef.current = null;
    };

    const scheduleRecoveryReplay = (question, turnId) => {
      if (!question || !turnId) return;
      cancelRecoveryReplay();
      recoveryTurnRef.current = { question, turnId };
      recoveryReplayTimerRef.current = setTimeout(() => {
        recoveryReplayTimerRef.current = null;
        const recovery = recoveryTurnRef.current;
        if (
          isInterviewTransportInterrupted() ||
          !recovery ||
          recovery.turnId !== activeTurnIdRef.current ||
          recovery.question !== question ||
          interviewEndedRef.current ||
          !isMountedRef.current
        ) {
          return;
        }

        if (activeQuestionTextRef.current) {
          displayAIQuestion(activeQuestionTextRef.current, turnId);
        }
        if (closingTurnRef.current === turnId) {
          showInterviewFlowError({code: "CLOSING_AUDIO_MISSING", turn_id: turnId, retryable: true,
            message: "The closing audio has not played. Please retry audio."});
          return;
        }
        console.warn("[RECOVERY] No provider audio arrived after reconnect; returning to Listening", {
          turnId,
        });
        recoveryTurnRef.current = null;
        window.__WAITING_FOR_TTS__ = false;
        window.__TTS_AUDIO_PLAYED__ = false;
        isPlayingStreamingRef.current = false;
        pendingAnswerRef.current = true;
        currentTurnShouldListenRef.current = true;
        markGeminiAnswerReady();
        safeSetStatus("Listening");
        scheduleRecognitionStart();
      }, 5000);
    };

    const cleanText = (txt) => {
      return txt.replace(/[^\w\s.,?!]/g, "");
    };

    const humanize = (text) =>
      text.replace(/\./g, "... ").replace(/\?/g, "? ... ").replace(/,/g, ", ");

    const beginAssistantTurn = () => {
      const shouldListen = nativeModeRef.current ? true : turnStateRef.current.beginAssistantTurn();
      const closing = closingTurnRef.current && closingTurnRef.current === activeTurnIdRef.current;
      currentTurnShouldListenRef.current = closing ? false : shouldListen;
      return closing ? false : shouldListen;
    };

    const drainQueuedQuestions = async () => {
      if (isInterviewTransportInterrupted() || interviewEndedRef.current) return;
      if (
        !canReceiveQuestionRef.current ||
        questionQueueRef.current.length === 0
      ) {
        return;
      }

      const nextQuestion = questionQueueRef.current.shift();
      if (!nextQuestion) return;

      canReceiveQuestionRef.current = false;
      pendingQuestionRef.current = nextQuestion;
      questionDisplayedRef.current = false;
      lastSentInterimRef.current = "";
      audioStreamFinalizedRef.current = false;

      const shouldListenAfterTurn = beginAssistantTurn();
      speak(
        nextQuestion,
        async () => {
          if (shouldListenAfterTurn) {
            markGeminiAnswerReady();
            // ✅ Clear TTS flags so speech recognition can start
            window.__TTS_AUDIO_PLAYED__ = false;
            window.__WAITING_FOR_TTS__ = false;
            await scheduleRecognitionStart();
            // ✅ WAIT for recognition to actually start before allowing next question
            await waitForRecognitionToStart(3000);
            console.log("✅ Speech recognition is active - ready for answer");
          }
          canReceiveQuestionRef.current = true;
          await drainQueuedQuestions();
        },
        () => {},
      );
    };

    const startCountdown = (seconds, onComplete) => {
      if (!isMountedRef.current) return;

      setCountdown(seconds);
      let remaining = seconds;

      const interval = setInterval(() => {
        if (!isMountedRef.current) {
          clearInterval(interval);
          return;
        }

        remaining -= 1;
        setCountdown(remaining);

        if (remaining <= 0) {
          clearInterval(interval);
          setCountdown(0);
          onComplete();
        }
      }, 1000);
    };

    const speak = async (text, onEnd, onAudioStart) => {
      const mediaGeneration = mediaGenerationRef.current;
      if (!isMediaGenerationCurrent(mediaGeneration)) return;
      if (role === "admin" || isPausedRef.current) {
        onEnd?.();
        return;
      }

      try {
        if (
          !socketRef.current ||
          socketRef.current.readyState !== WebSocket.OPEN
        ) {
          onEnd?.();
          return;
        }

        window.__WAITING_FOR_TTS__ = true;
        window.__TTS_AUDIO_PLAYED__ = false;
        pendingAnswerRef.current = false;
        resetAnswerForPlayback();
        safeSetStatus("AI Speaking...");
        window.__TTS_ON_END__ = onEnd;
        window.__TTS_ON_AUDIO_START__ = onAudioStart;

        let onEndCalledByAvatarHandler = false;
        const originalOnEnd = window.__TTS_ON_END__;
        window.__TTS_ON_END__ = (...args) => {
          onEndCalledByAvatarHandler = true;
          originalOnEnd?.(...args);
        };

        if (recognitionRef.current) {
          try {
            if (recognizingRef.current) {
              recognitionRef.current.stop();
              recognizingRef.current = false;
            }
          } catch (e) {
            console.warn("🎤 Error stopping recognition in speak():", e);
          }
        }

        await new Promise((resolve) => {
          ttsDoneResolverRef.current = resolve;
          ttsDoneResolverRef.current.__timeoutId = setTimeout(() => {
            if (ttsDoneResolverRef.current === resolve) {
              ttsDoneResolverRef.current = null;
              resolve();
            }
          }, 30000);
        });

        if (!isMediaGenerationCurrent(mediaGeneration)) return;
        if (!window.__TTS_AUDIO_PLAYED__ || onEndCalledByAvatarHandler) {
          return;
        }

        onEnd?.();
      } catch (err) {
        console.error("❌ [SPEAK ERROR]", err);
        if (isMediaGenerationCurrent(mediaGeneration)) onEnd?.();
      }
    };

    const safeStartRecognition = () => {
      if (codingHandoffRef.current) return;
      if (isInterviewTransportInterrupted()) {
        console.log("Recognition start blocked - interview transport is recovering");
        return;
      }
      if (interviewEndedRef.current) {
        console.log("Recognition start blocked - interview already ended");
        return;
      }
      if (role === "admin") return;
      if (!isMountedRef.current) return;
      if (isPausedRef.current) return;

      try {
        if (window.__WAITING_FOR_TTS__) {
          console.log(
            "🎤 safeStartRecognition: blocked by __WAITING_FOR_TTS__",
          );
          return;
        }
        if (window.__TTS_AUDIO_PLAYED__) {
          try {
            const last = window.__TTS_AUDIO_LAST_PLAYED_AT__ || 0;
            const age = Date.now() - last;
            if (!isPlayingStreamingRef.current && last && age > 600) {
              console.warn(
                "🎤 safeStartRecognition: clearing stale __TTS_AUDIO_PLAYED__ (age ms)",
                age,
              );
              window.__TTS_AUDIO_PLAYED__ = false;
              window.__WAITING_FOR_TTS__ = false;
              try {
                window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
              } catch (e) {}
            } else {
              console.log(
                "🎤 safeStartRecognition: blocked by __TTS_AUDIO_PLAYED__",
              );
              return;
            }
          } catch (e) {
            console.log(
              "🎤 safeStartRecognition: blocked by __TTS_AUDIO_PLAYED__",
            );
            return;
          }
        }
        if (isPlayingStreamingRef.current) {
          console.log(
            "🎤 safeStartRecognition: blocked by isPlayingStreamingRef",
          );
          return;
        }

        // ✅ Recognition guard: never start while Browser TTS is actively playing
        if (window.__WAITING_FOR_TTS__ === true) {
          console.log(
            "🎤 safeStartRecognition: guard blocked by __WAITING_FOR_TTS__",
          );
          return;
        }
        if (
          window.__TTS_AUDIO_PLAYED__ === true &&
          isPlayingStreamingRef.current
        ) {
          console.log(
            "🎤 safeStartRecognition: guard blocked by __TTS_AUDIO_PLAYED__ + isPlaying",
          );
          return;
        }

        if (!recognitionRef.current) {
          console.log("🎤 Recognition instance missing, initializing...");
          initRecognition();
        }

        if (recognitionRef.current && !recognizingRef.current) {
          const recognition = recognitionRef.current;
          const generation = mediaGenerationRef.current;
          const attemptStart = () => {
            if (!isMediaGenerationCurrent(generation) || recognitionRef.current !== recognition) return;
            try {
              lastSentInterimRef.current = "";
              recognitionStartAttemptsRef.current = 0;
              recognitionRef.current.start();
              console.log("🎤 SpeechRecognition.start() called successfully");
            } catch (err) {
              const msg =
                (err && err.toString && err.toString()) || String(err);
              console.warn("🎤 start() failed:", msg);
              recognitionStartAttemptsRef.current =
                (recognitionStartAttemptsRef.current || 0) + 1;

              if (msg.includes("already started")) {
                console.log("🎤 Recognition already started, no need to retry");
                recognitionStartAttemptsRef.current = 0;
                return;
              }

              if (recognitionStartAttemptsRef.current < 6) {
                const delayMs = 100 * recognitionStartAttemptsRef.current;
                console.log(
                  `🎤 Retrying recognition start (attempt ${recognitionStartAttemptsRef.current}/6) in ${delayMs}ms...`,
                );
                setTimeout(attemptStart, delayMs);
              } else if (recognitionStartAttemptsRef.current >= 6) {
                console.warn(
                  "🎤 start() failing repeatedly — recreating recognition instance",
                );
                try {
                  if (recognitionRef.current) {
                    try {
                      recognitionRef.current.onerror = null;
                      recognitionRef.current.onend = null;
                      recognitionRef.current.onresult = null;
                      recognitionRef.current.onstart = null;
                      recognitionRef.current.stop();
                    } catch (e) {}
                    recognitionRef.current = null;
                  }
                  recognitionStartAttemptsRef.current = 0;
                  if (!nativeModeRef.current) initRecognition();
                  setTimeout(() => {
                    try {
                      safeStartRecognition();
                    } catch (e) {}
                  }, 300);
                } catch (e) {
                  console.error("🎤 recreate recognition failed", e);
                }
              }
            }
          };

          // ✅ Delay is now handled by scheduleRecognitionStart(); just fire immediately here
          setTimeout(attemptStart, 0);
        } else if (recognitionRef.current && recognizingRef.current) {
          console.log("🎤 Recognition already active");
        }
      } catch (err) {
        console.error("🎤 safeStartRecognition error:", err);
      }
    };

    const stopRecognition = () => {
      if (recognitionRef.current && recognizingRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.warn("🎤 Error stopping recognition:", e);
        }
      }
    };

    const ensureRecognitionActive = () => {
      if (role === "admin" || !isMountedRef.current || isPausedRef.current)
        return;
      if (isInterviewTransportInterrupted()) return;

      if (
        pendingAnswerRef.current &&
        !recognizingRef.current &&
        !window.__WAITING_FOR_TTS__ &&
        !isPlayingStreamingRef.current
      ) {
        if (!recognitionRef.current) {
          console.log("🎤 Recognition null, reinitializing...");
          initRecognition();
        } else {
          console.log(
            "🎤 Recognition inactive, restarting to ensure voice capture...",
          );
          scheduleRecognitionStart();
        }
      }
    };

    const forceRecognitionReset = () => {
      if (role === "admin") return;
      if (!isMountedRef.current) return;

      console.warn(
        "🎤 Force resetting recognition due to potential stuck state",
      );
      try {
        if (recognitionRef.current) {
          try {
            recognitionRef.current.onerror = null;
            recognitionRef.current.onend = null;
            recognitionRef.current.onresult = null;
            recognitionRef.current.onstart = null;
            if (recognizingRef.current) {
              recognitionRef.current.stop();
            }
          } catch (e) {}
          recognitionRef.current = null;
        }
        recognizingRef.current = false;
        recognitionStartAttemptsRef.current = 0;
        initRecognition();
      } catch (e) {
        console.error("🎤 Error during force reset:", e);
      }
    };

    const resetSilenceTimer = () => {
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = setTimeout(() => {
        if (
          !isInterviewTransportInterrupted() &&
          recognizingRef.current &&
          recognitionRef.current &&
          pendingAnswerRef.current &&
          !isPlayingStreamingRef.current &&
          !window.__WAITING_FOR_TTS__
        ) {
          const text = bufferRef.current.trim();

          if (text.trim().length > 0) {
            console.log(
              "🎤 [AUTO-SUBMIT] 2.5 seconds of silence detected — sending answer_complete",
            );

            try {
              if (!sendAnswerComplete(text)) return;
            } catch (e) {
              console.error("❌ Failed to send answer_complete", e);
              return;
            }
            setMessages((prev) => {
              const updated = [...prev];
              let foundIndex = -1;
              for (let i = updated.length - 1; i >= 0; i--) {
                if (
                  updated[i].answer?.startsWith("🧑 You:") &&
                  !updated[i].isSubmitted
                ) {
                  foundIndex = i;
                  break;
                }
              }
              if (foundIndex >= 0) {
                updated[foundIndex].answer = "🧑 You: " + text;
                updated[foundIndex].isSubmitted = true;
              } else {
                updated.push({ answer: "🧑 You: " + text, isSubmitted: true });
              }
              return updated;
            });

            window.__WAITING_FOR_TTS__ = true;

            pendingAnswerRef.current = false;
            lastSentInterimRef.current = "";
            bufferRef.current = "";

            try {
              recognitionRef.current?.stop();
            } catch (e) {}
        } else {
          // The agent may still be speaking when the browser's recognition
          // silence timer fires. Keep the partial transcript; submitting it
          // here makes answers such as "so" become final answers.
          if (
            pendingAnswerRef.current &&
            (isPlayingStreamingRef.current || window.__WAITING_FOR_TTS__)
          ) {
            console.log(
              "🎤 [AUTO-SUBMIT] Delayed because agent audio is still active",
            );
            return;
          }
          try {
            recognitionRef.current?.stop();
            } catch (e) {}
          }
        }
      }, 1800);
    };

    const isValidAnswer = (text) => {
      if (!text) return false;
      const clean = text.trim().toLowerCase();
      if (clean.length === 0) return false;
      if (/^(uh|um|hmm|ah|eh)$/i.test(clean)) return false;
      return true;
    };

    // ── Mobile detection ────────────────────────────────────────────
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    // iPadOS reports as "MacIntel" with touch support, so it needs the extra check.
    const isIOS =
      /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // ── warmUpMicrophoneForRecognition kept for ONE-TIME use in socket.onopen ──
    // DO NOT call this anywhere else — repeated getUserMedia causes Android mic conflict.
    const warmUpMicrophoneForRecognition = async () => {
      if (!isMobile) return; // desktop: no-op
      try {
        console.log("🎤 [WARMUP] One-time mic warmup at interview start...");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        await new Promise((resolve) => setTimeout(resolve, 150));
        stream.getTracks().forEach((track) => track.stop());
        console.log("🎤 [WARMUP] Microphone warmed up (one-time only).");
      } catch (e) {
        console.warn("🎤 [WARMUP] Could not warm up microphone:", e);
      }
    };

    // ── scheduleRecognitionStart — SINGLE entry point for all STT restarts ──
    // Fix 7: every restart path calls this, never recognition.start() directly.
    // Fix 1: releases app mic BEFORE starting recognition on mobile.
    // Fix 5: 900ms delay on mobile so OS has time to yield mic ownership.
    // Fix 8: recognitionStartingRef lock prevents duplicate simultaneous starts.
    // Fix 4: timeout protection to prevent Android stuck if onstart never fires
    const scheduleRecognitionStart = async () => {
      if (nativeModeRef.current) {
        if (!isInterviewTransportInterrupted() && !interviewEndedRef.current && role !== "admin") {
          const key = `${activeTurnIdRef.current}:${lastAudioStreamSequenceRef.current}`;
          if (nativePlaybackAckRef.current !== key) {
            nativePlaybackAckRef.current = key;
            nativeListenAllowedRef.current = false;
            safeSetStatus("Please wait...");
            console.info("[PROVIDER_AUDIT] stage=browser_playback_complete provider=gemini_live_native_audio", { turn: activeTurnIdRef.current, stream: activeAudioStreamIdRef.current, sequence: lastAudioStreamSequenceRef.current });
            socketRef.current.send(JSON.stringify({ type: "native_playback_complete",
              turn_id: activeTurnIdRef.current, stream_id: activeAudioStreamIdRef.current,
              stream_sequence: lastAudioStreamSequenceRef.current }));
          }
        }
        return;
      }
      if (flowErrorRef.current || (closingTurnRef.current === activeTurnIdRef.current && closingTurnRef.current)) return;
      if (interviewEndedRef.current) {
        console.log("Recognition restart blocked - interview already ended");
        return;
      }
      if (isInterviewTransportInterrupted()) return;
      if (recognitionStartingRef.current) {
        console.log("🎤 Recognition already starting.");
        return;
      }

      const generation = mediaGenerationRef.current;
      const turnId = activeTurnIdRef.current;
      recognitionStartingRef.current = true;

      try {
        if (window.__RELEASE_APP_MIC_FOR_STT__) {
          await window.__RELEASE_APP_MIC_FOR_STT__();
        }

        const delay = isIOS ? 1800 : isMobile ? 1800 : 150;

        await new Promise((r) => setTimeout(r, delay));

        if (!isMediaGenerationCurrent(generation) || turnId !== activeTurnIdRef.current) return;
        safeStartRecognition();
      } catch (e) {
        console.error(e);
      } finally {
        // Release the start lock if recognition did not actually begin.
        if (!recognizingRef.current) {
          recognitionStartingRef.current = false;
        }
      }
    };

    // ✅ Wait for recognition to actually start (onstart event fired)
    const waitForRecognitionToStart = async (timeoutMs = 3000) => {
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          recognitionStartedResolverRef.current = null;
          console.warn("🎤 Timeout waiting for recognition.onstart");
          resolve(); // Resolve anyway to not block forever
        }, timeoutMs);

        recognitionStartedResolverRef.current = () => {
          clearTimeout(timeout);
          recognitionStartedResolverRef.current = null;
          console.log("🎤 Recognition.onstart confirmed - listening active");
          resolve();
        };
      });
    };

    const initRecognition = () => {
      if (nativeModeRef.current) return;
      if (role === "admin") return;
      if (recognitionRef.current) return;

      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        appendMessage("⚠️ Your browser doesn't support SpeechRecognition.");
        return;
      }

      const recognition = new SR();
      // ✅ Mobile fix: non-continuous on mobile avoids Android stuck-mic state
      recognition.continuous = !isMobile;
      recognition.interimResults = true;
      recognition.maxAlternatives = 3;
      recognition.lang = preferredLangRef.current;

      const recognitionGeneration = mediaGenerationRef.current;
      let recognitionTurnId = null;
      const isCurrentRecognition = () =>
        recognitionRef.current === recognition &&
        isMediaGenerationCurrent(recognitionGeneration);
      recognition.onstart = () => {
        if (!isCurrentRecognition()) {
          try { recognition.abort(); } catch (e) {}
          return;
        }
        recognitionTurnId = activeTurnIdRef.current;
        // Fix 8: unlock the start lock — STT is now active
        recognitionStartingRef.current = false;
        window.__MIC_STARTED__ = true; // ✅ Mobile state flag
        if (!isMountedRef.current) return;
        recognizingRef.current = true;
        recognitionStartAttemptsRef.current = 0;
        console.log("🎤 SpeechRecognition started - listening for voice input");
        // ✅ Resolve the waiting promise — recognition is now actually active
        if (recognitionStartedResolverRef.current) {
          const resolver = recognitionStartedResolverRef.current;
          recognitionStartedResolverRef.current = null;
          resolver();
        }
        if (
          !window.__WAITING_FOR_TTS__ &&
          !window.__TTS_AUDIO_PLAYED__ &&
          !isPlayingStreamingRef.current
        ) {
          safeSetStatus("Listening");
        }
      };

      recognition.onresult = (event) => {
        if (!isCurrentRecognition() || recognitionTurnId !== activeTurnIdRef.current) return;
        if (!isMountedRef.current) return;
        if (
          !pendingAnswerRef.current ||
          window.__WAITING_FOR_TTS__ === true ||
          window.__TTS_AUDIO_PLAYED__ === true ||
          isPlayingStreamingRef.current
        ) {
          console.warn("[STT] Ignoring recognition result outside candidate-answer window");
          return;
        }

        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript = event.results[i][0].transcript;
          // ✅ Voice detection log — critical for debugging mobile voice capture
          console.log("VOICE DETECTED:", transcript);
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
          } else {
            interimTranscript += transcript + " ";
          }
        }

        if (finalTranscript.trim()) {
          // Android can restart recognition several times for one answer. A
          // finalized result is usually only the latest segment, so replacing
          // the buffer loses everything spoken before it. Merge cumulative
          // and segment results while suppressing duplicate-result cases.
          const segment = finalTranscript.trim();
          const current = bufferRef.current.trim();
          const currentLower = current.toLocaleLowerCase();
          const segmentLower = segment.toLocaleLowerCase();

          if (!current) {
            bufferRef.current = segment;
          } else if (currentLower === segmentLower || currentLower.endsWith(` ${segmentLower}`)) {
            // Same result was emitted again.
          } else if (segmentLower.startsWith(`${currentLower} `)) {
            // Some engines return the complete cumulative transcript.
            bufferRef.current = segment;
          } else if (!currentLower.endsWith(segmentLower)) {
            bufferRef.current = `${current} ${segment}`.trim();
          }
        }

        const liveTranscript = (
          bufferRef.current +
          " " +
          interimTranscript
        ).trim();

        if (liveTranscript) {
          setMessages((prev) => {
            const updated = [...prev];
            let foundIndex = -1;
            for (let i = updated.length - 1; i >= 0; i--) {
              if (
                updated[i].answer &&
                updated[i].answer.startsWith("🧑 You:") &&
                !updated[i].isSubmitted
              ) {
                foundIndex = i;
                break;
              }
            }
            if (foundIndex >= 0) {
              updated[foundIndex].answer = "🧑 You: " + liveTranscript;
            } else {
              updated.push({
                answer: "🧑 You: " + liveTranscript,
                isSubmitted: false,
              });
            }
            return updated;
          });
        }

        try {
          if (
            socketRef.current &&
            socketRef.current.readyState === WebSocket.OPEN &&
            role !== "admin"
          ) {
            const liveToSend = liveTranscript.trim();
            if (liveToSend && liveToSend !== lastSentInterimRef.current) {
              lastSentInterimRef.current = liveToSend;
              socketRef.current.send(
                JSON.stringify({ type: "answer_partial", text: liveToSend, turn_id: recognitionTurnId }),
              );
            }
          }
        } catch (e) {}

        // const endInterviewCommand = /^(?:stop|end|quit)\s+(?:the\s+)?interview[.!?]?$/i.test(
        //   bufferRef.current.trim(),
        // );
        // if (endInterviewCommand) {
        //   interviewEndedRef.current = true;
        //   console.log("🎤 Voice end command detected; interviewEndedRef set to true");
        //   if (silenceTimeoutRef.current) {
        //     clearTimeout(silenceTimeoutRef.current);
        //     silenceTimeoutRef.current = null;
        //   }
        //   window.__WAITING_FOR_TTS__ = false;
        //   window.__TTS_AUDIO_PLAYED__ = false;
        //   try {
        //     window.speechSynthesis?.cancel();
        //   } catch (error) {}
        //   try {
        //     recognition.stop();
        //     console.log("🎤 Recognition stopped for interview end");
        //   } catch (error) {}
        //   if (socketRef.current?.readyState === WebSocket.OPEN) {
        //     socketRef.current.send(JSON.stringify({ type: "end_interview" }));
        //   }
        //   endInterview();
        //   return;
        // }

        resetSilenceTimer();
      };

      recognition.onerror = (e) => {
        if (!isCurrentRecognition()) return;
        // Fix 8: always unlock on error so next attempt can proceed
        recognitionStartingRef.current = false;
        if (!isMountedRef.current) return;
        console.warn("🎤 SpeechRecognition error:", e);
        const errorMsg = (e && e.error) || "unknown";
        if (errorMsg === "no-speech") {
          console.log("🎤 No speech detected, will continue listening");
        } else if (errorMsg === "network") {
          console.error("🎤 Network error in speech recognition");
        } else if (
          errorMsg === "not-allowed" ||
          errorMsg === "service-not-allowed"
        ) {
          console.error("🎤 Mic permission blocked:", errorMsg);
          if (isIOS) {
            appendMessage(
              "⚠️ Microphone access is blocked. On iPhone, check Settings → Safari → Microphone (or the 'aA' menu → Website Settings) and allow access, then reload.",
            );
          }
        } else {
          console.log("🎤 SpeechRecognition error code:", errorMsg);
        }
      };

      recognition.onend = async () => {
        if (!isCurrentRecognition()) return;
        if (recognitionTurnId !== activeTurnIdRef.current) {
          recognizingRef.current = false;
          recognitionStartingRef.current = false;
          window.__MIC_STARTED__ = false;
          return;
        }
        if (interviewEndedRef.current) {
          console.log("Recognition onend ignored - interview already ended");
          return;
        }
        // Fix 8: unlock start lock
        recognitionStartingRef.current = false;
        if (!isMountedRef.current) return;
        if (isInterviewTransportInterrupted()) {
          console.log("Recognition onend ignored - interview transport is recovering");
          return;
        }
        window.__MIC_STARTED__ = false; // ✅ Mobile state flag
        recognizingRef.current = false;
        let shouldRestoreMic = true;
        console.log(
          "🎤 SpeechRecognition ended | pendingAnswer:",
          pendingAnswerRef.current,
          "| isPlayingAudio:",
          isPlayingStreamingRef.current,
        );

        if (
          isPlayingStreamingRef.current ||
          window.__WAITING_FOR_TTS__
        ) {
          console.log(
            "🎤 Recognition ended while agent audio/TTS was active; preserving partial answer",
          );
          shouldRestoreMic = false;
          return;
        }

        const text = bufferRef.current.trim();

        if (pendingCommandRef.current) {
          if (text.toLowerCase().includes("start interview")) {
            socketRef.current.send("Start Interview");
            pendingCommandRef.current = false;
          } else if (text) {
            appendMessage({ question: "🔄 Please say 'Start Interview'" });
            if (!recognizingRef.current && isMountedRef.current) {
              shouldRestoreMic = false;
              scheduleRecognitionStart();
            }
          } else {
            if (!recognizingRef.current && isMountedRef.current) {
              shouldRestoreMic = false;
              scheduleRecognitionStart();
            }
          }
        } else if (pendingAnswerRef.current) {
          console.log("🎤 [ONEND] Recognition ended, text captured:", text);

          // WEB/DESKTOP: onend does NOT mean the candidate finished speaking.
          // Wait for the 3-second silence timer instead.
          if (!isMobile) {
            console.log(
              "🎤 [WEB] Recognition ended - waiting for silence timer",
            );

            if (pendingAnswerRef.current && isMountedRef.current) {
              shouldRestoreMic = false;
              scheduleRecognitionStart();
            }

            return;
          }

          if (text && isValidAnswer(text)) {
            if (pendingAnswerRef.current) {
              isPlayingStreamingRef.current = false;
              audioQueueRef.current = [];
              lastProcessedIndexRef.current = -1;
              nextPlayTimeRef.current = 0;

              const answerText =
                text.trim().toLowerCase() === "next" ? "next" : text;

              try {
                if (!sendAnswerComplete(answerText)) {
                  pendingAnswerRef.current = true;
                  return;
                }
              } catch (e) {
                console.warn("[ANSWER] Final answer was not sent", e);
                return;
              }

              // Fix 2 is now handled at the top of onend (always restores after every session)

              window.__WAITING_FOR_TTS__ = true;

              setMessages((prev) => {
                const updated = [...prev];
                let foundIndex = -1;
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (
                    updated[i].answer?.startsWith("🧑 You:") &&
                    !updated[i].isSubmitted
                  ) {
                    foundIndex = i;
                    break;
                  }
                }
                if (foundIndex >= 0) {
                  updated[foundIndex].answer = "🧑 You: " + answerText;
                  updated[foundIndex].isSubmitted = true;
                } else {
                  updated.push({
                    answer: "🧑 You: " + answerText,
                    isSubmitted: true,
                  });
                }
                return updated;
              });

              pendingAnswerRef.current = false;
              canReceiveQuestionRef.current = true;
              bufferRef.current = "";
            }
          } else if (text) {
            console.log("🎤 Invalid answer received, asking to repeat");
            appendMessage({
              question: "⚠️ Please repeat your answer clearly.",
            });
            if (pendingAnswerRef.current) {
              if (!recognizingRef.current && isMountedRef.current) {
                console.log("🎤 Restarting recognition after invalid answer");
                bufferRef.current = "";
                shouldRestoreMic = false;
                scheduleRecognitionStart();
              }
            }
          } else {
            console.log(
              "🎤 No speech detected, restarting recognition to continue listening",
            );
            if (pendingAnswerRef.current) {
              if (!recognizingRef.current && isMountedRef.current) {
                console.log("🎤 Restarting recognition after silence");
                bufferRef.current = "";
                shouldRestoreMic = false;
                scheduleRecognitionStart();
              }
            }
          }
        }
        if (shouldRestoreMic && window.__RESTORE_APP_MIC_AFTER_STT__) {
          await window.__RESTORE_APP_MIC_AFTER_STT__();
        }
      };

      recognitionRef.current = recognition;
    };

    const startInterview = () => {
      if (interviewEndedRef.current || codingHandoffRef.current || !isMountedRef.current) return;
      if (!networkReachableRef.current) {
        reconnectInFlightRef.current = false;
        showConnectionInterrupted();
        safeSetStatus("Reconnecting");
        return;
      }
      // Prevent duplicate WebSocket connections.
      // If the current socket is already CONNECTING or OPEN,
      // do not create another one.
      const existingSocket = socketRef.current;

      if (
        existingSocket &&
        (existingSocket.readyState === WebSocket.CONNECTING ||
          existingSocket.readyState === WebSocket.OPEN)
      ) {
        console.log(
          "[WebSocket] Connection already in progress/active. Skipping duplicate connect.",
          existingSocket.readyState === WebSocket.CONNECTING
            ? "CONNECTING"
            : "OPEN",
        );
        return;
      }
      if (
        !interviewId ||
        String(interviewId).trim() === "" ||
        String(interviewId) === "undefined" ||
        String(interviewId) === "null"
      ) {
        notify("error", "Interview ID is missing. Cannot start interview.");
        console.error(
          "❌ [VoiceInterview] interviewId is",
          interviewId,
          "— aborting WebSocket connection",
        );
        return;
      }

      if (!languageReadyRef.current) {
        notify(
          "error",
          "Interview language is still loading. Please try again.",
        );
        return;
      }

      const requiresBrowserStt = scheduledSttProviderRef.current === "browser" &&
        process.env.REACT_APP_INTERVIEW_ENGINE !== "gemini_native";
      if (
        requiresBrowserStt &&
        !(window.SpeechRecognition || window.webkitSpeechRecognition)
      ) {
        appendMessage({
          answer: `This interview cannot be started on your current browser because the interview language, ${interviewLanguageRef.current.displayName}, is not supported. Please use a supported browser.`,
        });
        notify(
          "error",
          "Speech recognition is not available for this browser and interview language.",
        );
        return;
      }

      if (isBrowserOnlyModeRef.current && process.env.REACT_APP_INTERVIEW_ENGINE !== "gemini_native") {
        if (!window.speechSynthesis || !initializeBrowserTtsVoice()) {
          appendMessage({
            answer: `This interview cannot be started on your current browser because the interview language, ${interviewLanguageRef.current.displayName}, is not supported. Please use a supported browser.`,
          });
          notify(
            "error",
            `No browser voice is available for ${interviewLanguageRef.current.displayName}.`,
          );
          return;
        }
      }

      const socket = new WebSocket(
        `${WS_BASE}/interview/${interviewId}?role=${role}`,
      );
      const socketGeneration = ++socketGenerationRef.current;
      const isCurrentSocket = () =>
        socketRef.current === socket &&
        socketGenerationRef.current === socketGeneration;
      socketRef.current = socket;
      clearSocketConnectTimeout();
      socketConnectTimeoutRef.current = setTimeout(() => {
        if (
          socketRef.current === socket &&
          socket.readyState === WebSocket.CONNECTING
        ) {
          interruptInterviewConnection();
        }
      }, SOCKET_CONNECT_TIMEOUT_MS);

      socket.onopen = async () => {
        if (
          !isCurrentSocket() ||
          interviewEndedRef.current ||
          !isMountedRef.current
        ) {
          console.warn("[WS] stale socket callback ignored: onopen", socketGeneration);
          return;
        }
        lastInterviewSocketTrafficAtRef.current = Date.now();
        networkReachableRef.current = true;
        clearSocketConnectTimeout();
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        if (!connectionInterruptedRef.current) {
          reconnectInFlightRef.current = false;
          reconnectAttemptRef.current = 0;
          setConnectionUi({ state: "connected", secondsRemaining: 0 });
        }
        startSocketHeartbeat(socket);
        appendMessage("🔗 Connected to server.");

        // Do not infer Listening from transport recovery. The backend sends
        // an interview_turn recovery message; only a completed AI turn may
        // start recognition.
        restoreListeningAfterReconnectRef.current = false;

        if (role !== "admin") {
          try {
            const systemInfo = await collectBrowserSystemInfo();
            if (!isCurrentSocket() || socket.readyState !== WebSocket.OPEN) {
              console.warn("[WS] stale socket callback ignored: metadata send", socketGeneration);
              return;
            }
            socket.send(JSON.stringify(systemInfo));
          } catch (error) {
            // Metadata logging is optional and must never affect the interview.
          }
        }

        // try {
        //   const res = await fetch(`${API_BASE}/interviews/${interviewId}`);
        //   const data = await res.json();

        //   preferredLangRef.current = LANG_MAP[data.preferred_language] || "en-US";

        //   console.log("🎤 Speech language:", preferredLangRef.current);
        // } catch (e) {
        //   console.warn("🎤 Language fetch failed, using English");
        // }

        if (role !== "admin") {
          // ✅ Mobile fix: request mic permission BEFORE initialising recognition
          //    so Android grants hardware access before the interview begins.
          try {
            console.log("🎤 [PERMISSION] Requesting microphone permission...");
            const permissionStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            });
            permissionStream.getTracks().forEach((track) => track.stop());
            console.log("🎤 [PERMISSION] Microphone permission granted.");
            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (e) {
            console.warn(
              "🎤 [PERMISSION] Microphone permission request failed:",
              e,
            );
          }
          if (!isCurrentSocket() || interviewEndedRef.current) return;
          if (scheduledSttProviderRef.current === "browser") {
            setTimeout(async () => {
              if (isCurrentSocket() && !interviewEndedRef.current &&
                  !nativeModeRef.current && !recognitionRef.current) initRecognition();
            }, 1000);
          }
        }
      };

      socket.onmessage = async (evt) => {
        if (!isMountedRef.current || !isCurrentSocket()) {
          console.warn("[WS] stale socket callback ignored: onmessage", socketGeneration);
          return;
        }
        if (interviewEndedRef.current) {
          // Final transcript can race with the parent's completed state.
          // Accept only closing text; do not restart audio/mic or the interview.
          try { displayClosingTurn(JSON.parse(evt.data)); } catch (e) {}
          console.log("WebSocket message ignored - interview already ended");
          return;
        }

        const msg = evt.data;
        lastInterviewSocketTrafficAtRef.current = Date.now();
        networkReachableRef.current = true;

        try {
          const healthMessage = JSON.parse(msg);
          if (healthMessage.type === "connection_health_ack") {
            let acknowledged = false;
            if (heartbeatSocketRef.current === socket) {
              const pending = heartbeatPendingRef.current;
              if (pending && pending.id === healthMessage.heartbeat_id) {
                const now = Date.now();
                lastHeartbeatAckRef.current = now;
                lastInterviewSocketTrafficAtRef.current = now;
                heartbeatPendingRef.current = null;
                acknowledged = true;
              }
            }
            if (acknowledged && connectionInterruptedRef.current) {
              showConnectionRestored({ fromInterviewSocket: true });
            }
            return;
          }
        } catch (error) {
          // Non-JSON interview messages continue through the existing flow.
        }

        // Connection is demonstrably working again when interview traffic
        // reaches the fresh socket. Heartbeat acks are handled above and do
        // not count as interview progress.
        if (socketRef.current === socket && connectionInterruptedRef.current) {
          console.log(
            "[ConnectionUI] Interview WebSocket traffic received after interruption. Restoring UI.",
          );

          showConnectionRestored({ fromInterviewSocket: true });
        }

        // ✅ DEBUG: Log all incoming messages
        try {
          const data = JSON.parse(msg);
          console.log(
            `📥 [WEBSOCKET MESSAGE] Type: ${data.type}, Size: ${msg.length}`,
          );
        } catch (e) {
          console.log(
            `📥 [WEBSOCKET MESSAGE] Raw text: ${msg.substring(0, 50)}...`,
          );
        }

        try {
          const data = JSON.parse(msg);

          if (data.type === "gemini_native_mode") {
            nativeModeRef.current = data.enabled === true;
            console.info("[PROVIDER_AUDIT] stage=browser_route", { engine: nativeModeRef.current ? "gemini_native" : "existing", role });
            if (!nativeModeRef.current) {
              if (scheduledSttProviderRef.current === "browser") initRecognition();
              return;
            }
            clearTimeout(silenceTimeoutRef.current);
            const recognition = recognitionRef.current;
            recognitionRef.current = null;
            if (recognition) {
              recognition.onstart = recognition.onresult = recognition.onend = recognition.onerror = null;
              try { recognition.abort(); } catch (e) {}
            }
            recognizingRef.current = false;
            recognitionStartingRef.current = false;
            if (role !== "admin" && !nativeCaptureRef.current) {
              const capture = createNativeCapture({
                socket, getTurnId: () => activeTurnIdRef.current,
                canCapture: () => isCurrentSocket() && !isInterviewTransportInterrupted() &&
                  !interviewEndedRef.current && !isPausedRef.current &&
                  // The server sends native_listening only after it has
                  // received the browser's real playback-complete callback.
                  // That is the authoritative microphone gate. Playback UI
                  // flags can clear a little later; using them here dropped
                  // delayed answers even though the candidate was listening.
                  nativeListenAllowedRef.current,
                onReady: () => {
                  if (!isCurrentSocket() || nativeCaptureRef.current !== capture ||
                      interviewEndedRef.current) {
                    capture.stop();
                    return;
                  }
                  nativeCaptureReadyRef.current = true;
                  // Listening can arrive while getUserMedia/start is pending.
                  if (nativeListenAllowedRef.current) capture.beginTurn();
                  console.info("[GEMINI_NATIVE] microphone capture ready");
                },
                onError: () => {
                  if (!isCurrentSocket() || nativeCaptureRef.current !== capture ||
                      isInterviewTransportInterrupted() || interviewEndedRef.current) return;
                  showInterviewFlowError({code: "NATIVE_MIC_UNAVAILABLE",
                    message: "Microphone streaming could not start. Please use End Interview and retry.", retryable: false});
                },
              });
              nativeCaptureRef.current = capture;
              void capture.start();
            }
            console.info("[GEMINI_NATIVE] full voice mode enabled; browser SpeechRecognition disabled");
            return;
          }
          if (data.type === "native_recovering") {
            nativeListenAllowedRef.current = false;
            nativePlaybackAckRef.current = null;
            audioStreamGenerationRef.current += 1;
            stopSharedAIPlayback();
            stopTrackedAudioSources();
            geminiPcmQueueRef.current = [];
            geminiActiveBatchesRef.current.clear();
            isPlayingStreamingRef.current = false;
            audioStreamFinalizedRef.current = false;
            markAudioPlaybackStarted(false);
            pendingAnswerRef.current = false;
            window.__WAITING_FOR_TTS__ = false;
            updateFlowError(null);
            safeSetStatus("Please wait...");
            armWaitingGuard();
            console.warn("[GEMINI_NATIVE] restoring upstream conversation", data.attempt);
            return;
          }
          if (data.type === "native_ai_transcript") {
            if (role === "admin") {
              displayAIQuestion(data.text, data.turn_id);
              return;
            }
            if (data.turn_id !== activeTurnIdRef.current) return;
            activeQuestionTextRef.current = data.text;
            pendingQuestionRef.current = data.text;
            if (audioPlaybackStartedRef.current || closingTurnRef.current === data.turn_id) {
              displayAIQuestion(data.text, data.turn_id);
            }
            return;
          }
          if (data.type === "native_candidate_transcript") {
            if (data.source === "repeat_control" && data.turn_id) {
              if (data.turn_id === activeTurnIdRef.current) {
                bufferRef.current = "";
                lastSentInterimRef.current = "";
                pendingAnswerRef.current = false;
                if (submittedTurnIdRef.current === data.turn_id) {
                  submittedTurnIdRef.current = null;
                }
                if (lastSubmittedAnswerRef.current?.turnId === data.turn_id) {
                  lastSubmittedAnswerRef.current = null;
                }
                if (reconnectAnswerRef.current?.turnId === data.turn_id) {
                  reconnectAnswerRef.current = null;
                }
                if (recoveredAnswerTurnRef.current === data.turn_id) {
                  recoveredAnswerTurnRef.current = null;
                }
              }
              setMessages(prev => prev.filter(row => !(row.turnId === data.turn_id && row.answer)));
              return;
            }
            if (!data.turn_id || !data.text) return;
            setMessages((prev) => {
              const index = prev.findIndex(row => row.turnId === data.turn_id && row.answer);
              if (index >= 0 && prev[index].isSubmitted && !data.final) return prev;
              const row = { answer: "🧑 You: " + data.text, turnId: data.turn_id,
                isSubmitted: Boolean(data.final), transcriptSource: data.source };
              const result = [...prev];
              if (index >= 0) result[index] = row;
              else {
                const questionIndex = result.findIndex(item => item.aiTurnId === data.turn_id);
                if (questionIndex >= 0) result.splice(questionIndex + 1, 0, row);
                else result.push(row);
              }
              return result;
            });
            return;
          }
          if (data.type === "native_listening") {
            if (data.turn_id !== activeTurnIdRef.current || role === "admin") return;
            clearWaitingGuard();
            if (nativeCaptureReadyRef.current) nativeCaptureRef.current?.beginTurn();
            nativeListenAllowedRef.current = true;
            pendingAnswerRef.current = true;
            safeSetStatus("Listening");
            return;
          }

          if (data.type === "interview_turn") {
            updateFlowError(null);
            if (closingTurnRef.current !== data.turn_id) {
              closingAudioStartedRef.current = false;
              closingAckSentRef.current = false;
            }
            closingTurnRef.current = data.message_kind === "closing" ? data.turn_id : null;
            if (data.turn_id) {
              if (activeTurnIdRef.current !== data.turn_id) {
                // Canonical turn changes invalidate old Gemini playback and callbacks.
                if (currentAudioProviderRef.current === "gemini" &&
                    avatarModeRef.current !== "custom" && avatarModeRef.current !== "did") {
                  audioStreamGenerationRef.current += 1;
                  stopSharedAIPlayback();
                  stopTrackedAudioSources();
                  geminiPcmQueueRef.current = [];
      geminiActiveBatchesRef.current.clear();
                  geminiPlaybackStartTimeRef.current = 0;
                  isPlayingStreamingRef.current = false;
                }
                activeAudioStreamIdRef.current = null;
                activeAudioTurnIdRef.current = null;
                activeAudioQuestionRef.current = "";
                lastAudioStreamSequenceRef.current = 0;
                reconnectAnswerRef.current = null;
                recoveredAnswerTurnRef.current = null;
                lastSubmittedAnswerRef.current = null;
                markAudioPlaybackStarted(false);
                // A result from the previous recognition session must never
                // acquire the next question's turn_id.
                const recognition = recognitionRef.current;
                recognitionRef.current = null;
                if (recognition) {
                  recognition.onstart = recognition.onresult = recognition.onend = recognition.onerror = null;
                  try { recognition.abort(); } catch (e) {}
                }
                recognizingRef.current = false;
                recognitionStartingRef.current = false;
                window.__MIC_STARTED__ = false;
                clearTimeout(silenceTimeoutRef.current);
                silenceTimeoutRef.current = null;
                pendingAnswerRef.current = false;
                bufferRef.current = "";
                lastSentInterimRef.current = "";
              }
              activeTurnIdRef.current = data.turn_id;
              if (nativeModeRef.current) {
                activeQuestionTextRef.current = data.question || "";
                pendingQuestionRef.current = data.question || "";
                nativeListenAllowedRef.current = false;
              }
              armWaitingGuard(data.turn_id);
            }
            if (data.question && data.turn_id) {
              // Receipt is not playback. Keep the canonical text for the
              // actual audio-start callback and for disconnect recovery.
              activeQuestionTextRef.current = data.question;
              pendingQuestionRef.current = data.question;
            }
            // Closing text must survive finalization even if its audio-start
            // callback is cancelled. Audio/replay will deduplicate this row.
            displayClosingTurn(data);
            if (Object.prototype.hasOwnProperty.call(data, "mode")) {
              const recoveredAnswer = reconnectAnswerRef.current;
              if (
                (data.replay_required || data.mode === "LISTENING") &&
                !data.answer_finalized &&
                !data.answer_queued &&
                recoveredAnswer?.turnId === data.turn_id &&
                recoveredAnswer.text
              ) {
                recoveredAnswerTurnRef.current = data.turn_id;
                bufferRef.current = recoveredAnswer.text;
                pendingAnswerRef.current = true;
                lastSentInterimRef.current = recoveredAnswer.text;
                if (socket.readyState === WebSocket.OPEN) {
                  socket.send(
                    JSON.stringify({
                      type: "answer_partial",
                      text: recoveredAnswer.text,
                      turn_id: data.turn_id,
                    }),
                  );
                }
                reconnectAnswerRef.current = null;
              } else if (data.answer_finalized || data.answer_queued) {
                reconnectAnswerRef.current = null;
                recoveredAnswerTurnRef.current = null;
                lastSubmittedAnswerRef.current = null;
              }

              if (typeof window.__RESTORE_APP_MIC_FOR_RECORDING__ === "function") {
                window.__RESTORE_APP_MIC_FOR_RECORDING__()
                  .then((result) => {
                    if (result?.restored === false && !result?.deferred) {
                      console.error("[AUDIO] Recording microphone restoration failed after reconnect");
                    }
                  })
                  .catch((error) => {
                    console.error("[AUDIO] Recording microphone restoration failed after reconnect", error);
                  });
              }

              // A locally sent answer may have been lost before the server
              // received it. Recovery state, rather than send(), decides this.
              if ((data.replay_required || data.mode === "LISTENING") &&
                  !data.answer_finalized && !data.answer_queued &&
                  submittedTurnIdRef.current === data.turn_id) {
                submittedTurnIdRef.current = null;
                setMessages((prev) => {
                  const updated = [...prev];
                  for (let i = updated.length - 1; i >= 0; i--) {
                    if (updated[i].turnId === data.turn_id && updated[i].answer?.startsWith("🧑 You:")) {
                      updated[i] = { ...updated[i], isSubmitted: false };
                      break;
                    }
                  }
                  return updated;
                });
              }
              const resumeListening = data.mode === "LISTENING" &&
                !data.replay_required && !data.answer_finalized && !data.answer_queued;
              safeSetStatus(
                data.replay_required
                  ? "AI Speaking..."
                  : resumeListening ? "Listening" : "Please wait...",
              );
              if (resumeListening && !nativeModeRef.current && role !== "admin") {
                clearWaitingGuard();
                pendingAnswerRef.current = true;
                currentTurnShouldListenRef.current = true;
                // Status text alone does not reopen the microphone.
                scheduleRecognitionStart();
              }
            }
            if (data.question && data.replay_required) {
              console.info("[RECOVERY] Replaying active interview turn", {
                turnId: data.turn_id,
              });
              scheduleRecoveryReplay(data.question, data.turn_id);
            }
            return;
          }

          if (data.type === "interview_flow_error") {
            showInterviewFlowError(data);
            return;
          }
          if (data.type === "interview_completed") {
            if (data.turn_id !== closingTurnRef.current || completionRequestedRef.current) return;
            completionRequestedRef.current = true;
            interviewEndedRef.current = true;
            clearWaitingGuard();
            updateFlowError(null);
            pendingAnswerRef.current = false;
            safeSetStatus("Interview completed");
            Promise.resolve().then(() => endInterview?.()).catch((error) => {
              console.error("Interview completion failed", error);
              completionRequestedRef.current = false;
              interviewEndedRef.current = false;
              showInterviewFlowError({code: "FINALIZATION_FAILED", retryable: false,
                message: "The interview could not be finalized. Please use End Interview to retry."});
            });
            return;
          }

          if (data.type === "end_interview_requested") {
            console.log("🎯 End interview request received from backend");
            onEndInterviewRequested?.();
            return;
          }
        } catch (e) {
          // Not JSON, continue
        }

        // ✅ FIX 1: FORWARD CUSTOM AVATAR FRAMES TO SERVICE (TOP PRIORITY)
        try {
          const data = JSON.parse(msg);

          if (data.type === "custom_avatar_video_frame") {
            console.log(
              "🎬 [FIX] Forwarding frame to AvatarStreamService:",
              data.frame_index,
            );
            avatarStreamService.handleCustomAvatarFrame(
              data.frame_data,
              data.frame_index,
            );
            return; // 🚨 VERY IMPORTANT - stop further processing
          }

          if (data.type === "custom_avatar_ready") {
            console.log("🎬 [FIX] Custom avatar ready - switching mode");
            avatarModeRef.current = "custom"; // ← update ref so audio_chunks route correctly
            avatarStreamService.emit("mode-changed", { mode: "custom" });
            return;
          }
        } catch (e) {
          // Not JSON, continue
        }

        // ✅ Handle browser_tts messages from backend
        try {
          const data = JSON.parse(msg);
          if (data.type === "browser_tts") {
            // A native no-response reminder is not a new interviewer turn.
            // Preserve the visible/current question and pause mic transport
            // only while the reminder itself is playing.
            const isNativeNoResponseReminder = Boolean(
              nativeModeRef.current && data.native_no_response_reminder,
            );
            cancelRecoveryReplay(data.text);
            console.log("🎤 [BROWSER-TTS] Received text:", data.text);

            if (window.speechSynthesis && data.text) {
              // Queue this message if one is already playing
              if (browserTtsPlayingRef.current) {
                console.log(
                  "🎤 [BROWSER-TTS] Queueing message (already playing)",
                );
                browserTtsQueueRef.current.push(data.text);
                return;
              }

              // Mark that we're now playing a browser TTS
              browserTtsPlayingRef.current = true;
              if (isNativeNoResponseReminder) {
                nativeListenAllowedRef.current = false;
              }

              flushGeminiAudioBuffer();
              // ✅ STOP recognition before speaking
              if (recognitionRef.current && recognizingRef.current) {
                try {
                  recognitionRef.current.stop();
                  recognizingRef.current = false;
                } catch (e) {}
              }

              // ✅ Allow TTS to survive socket close
              window.__ALLOW_TTS_TO_FINISH__ = true;

              const mediaGeneration = mediaGenerationRef.current;
              const ttsTurnId = activeTurnIdRef.current;
              const playbackTurnId = activeTurnIdRef.current;
              const utterance = new SpeechSynthesisUtterance(data.text);

              const selectedGender =
                avatarGenderRef.current === "female" ? "female" : "male";

              // 🔒 Always use the same voice selected for this interview
              const selectedVoice = initializeBrowserTtsVoice();

              utterance.lang = data.locale || preferredLangRef.current;
              utterance.rate = 1.0;
              utterance.pitch = 1.0;

              if (!selectedVoice) {
                console.error(
                  "❌ [BROWSER-TTS] No browser TTS voice available at all.",
                  {
                    gender: selectedGender,
                    language: preferredLangRef.current,
                  },
                );
                notify(
                  "error",
                  `No browser voice is available for ${data.language || interviewLanguageRef.current?.displayName || "the selected interview language"}.`,
                );
                browserTtsPlayingRef.current = false;
                if (isNativeNoResponseReminder) {
                  nativeListenAllowedRef.current = true;
                  pendingAnswerRef.current = true;
                  safeSetStatus("Listening");
                }
                return;
              }

              utterance.voice = selectedVoice;

              console.log("🔒 [BROWSER-TTS] Final locked voice:", {
                gender: selectedGender,
                voice: selectedVoice.name,
                language: selectedVoice.lang,
                voiceURI: selectedVoice.voiceURI,
                localService: selectedVoice.localService,
              });
              const shouldListenForThisTurn = isNativeNoResponseReminder
                ? true
                : beginAssistantTurn();

              utterance.onstart = () => {
                if (!isMediaGenerationCurrent(mediaGeneration)) return;
                markAudioPlaybackStarted(true);
                console.log("🗣️ TTS STARTED");
                if (!isNativeNoResponseReminder) {
                  displayAIQuestion(data.text, ttsTurnId);
                }
                pendingAnswerRef.current = false;
                resetAnswerForPlayback();
                // ✅ Stop recognition before TTS speaks — never run both simultaneously
                if (recognitionRef.current && recognizingRef.current) {
                  try {
                    recognitionRef.current.stop();
                    recognizingRef.current = false;
                  } catch (e) {}
                }
                safeSetStatus("AI Speaking...");
                dispatchBrowserTtsEvent("browser_tts_start");
                // 🍎 iOS: keep the utterance alive past its ~15s auto-pause bug
                startTtsKeepAlive();
              };

              utterance.onend = async () => {
                if (!isMediaGenerationCurrent(mediaGeneration)) return;
                markAudioPlaybackStarted(false);
                if (interviewEndedRef.current) {
                  console.log("TTS restart blocked because interview ended");
                  clearTtsKeepAlive();
                  return;
                }
                console.log("✅ TTS FINISHED");
                clearTtsKeepAlive();
                window.__ALLOW_TTS_TO_FINISH__ = false;
                window.__WAITING_FOR_TTS__ = false;
                window.__TTS_AUDIO_PLAYED__ = true;
                window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
                if (isNativeNoResponseReminder) {
                  // Resume exactly the existing native turn. Do not request a
                  // new question or emit another playback acknowledgement.
                  nativeListenAllowedRef.current = true;
                  pendingAnswerRef.current = true;
                  safeSetStatus("Listening");
                  browserTtsPlayingRef.current = false;
                  return;
                }
                if (dispatchBrowserTtsEvent("browser_tts_end", playbackTurnId)) return;
                if (ttsDoneResolverRef.current) {
                  try {
                    clearTimeout(ttsDoneResolverRef.current.__timeoutId);
                  } catch (e) {}
                  const resolver = ttsDoneResolverRef.current;
                  ttsDoneResolverRef.current = null;
                  resolver();
                }
                const onEndCallback = window.__TTS_ON_END__;
                window.__TTS_ON_END__ = null;
                if (onEndCallback) {
                  try {
                    onEndCallback();
                  } catch (e) {
                    console.error("🎤 Error in onEnd callback:", e);
                  }
                }
                if (shouldListenForThisTurn) {
                  safeSetStatus("Listening");
                  markGeminiAnswerReady();
                  // ✅ Clear TTS flags so speech recognition can start
                  window.__TTS_AUDIO_PLAYED__ = false;
                  window.__WAITING_FOR_TTS__ = false;
                  await scheduleRecognitionStart();
                  // ✅ WAIT for recognition to actually start before allowing next question
                  await waitForRecognitionToStart(3000);
                  console.log(
                    "✅ Speech recognition is active - ready for answer",
                  );
                } else {
                  safeSetStatus("AI Speaking...");
                }
                canReceiveQuestionRef.current = true;
                await drainQueuedQuestions();

                // ✅ Process queued browser_tts messages
                browserTtsPlayingRef.current = false;
                if (browserTtsQueueRef.current.length > 0) {
                  const nextText = browserTtsQueueRef.current.shift();
                  console.log(
                    "🎤 [BROWSER-TTS] Processing queued message:",
                    nextText.substring(0, 60),
                  );
                  // Trigger browser_tts handling by simulating a message
                  socket.onmessage({
                    data: JSON.stringify({
                      type: "browser_tts",
                      text: nextText,
                    }),
                  });
                }
              };

              utterance.onerror = async (error) => {
                if (!isMediaGenerationCurrent(mediaGeneration)) return;
                markAudioPlaybackStarted(false);
                if (interviewEndedRef.current) {
                  console.log("TTS restart blocked because interview ended");
                  clearTtsKeepAlive();
                  return;
                }
                console.error("❌ TTS ERROR:", error);
                clearTtsKeepAlive();
                window.__ALLOW_TTS_TO_FINISH__ = false;
                window.__WAITING_FOR_TTS__ = false;
                window.__TTS_AUDIO_PLAYED__ = true;
                window.__TTS_AUDIO_LAST_PLAYED_AT__ = Date.now();
                if (isNativeNoResponseReminder) {
                  nativeListenAllowedRef.current = true;
                  pendingAnswerRef.current = true;
                  safeSetStatus("Listening");
                  browserTtsPlayingRef.current = false;
                  return;
                }
                if (dispatchBrowserTtsEvent("browser_tts_end", playbackTurnId)) return;
                if (ttsDoneResolverRef.current) {
                  try {
                    clearTimeout(ttsDoneResolverRef.current.__timeoutId);
                  } catch (e) {}
                  const resolver = ttsDoneResolverRef.current;
                  ttsDoneResolverRef.current = null;
                  resolver();
                }
                const onEndCallback = window.__TTS_ON_END__;
                window.__TTS_ON_END__ = null;
                if (onEndCallback) {
                  try {
                    onEndCallback();
                  } catch (e) {
                    console.error("🎤 Error in onEnd callback:", e);
                  }
                }
                if (shouldListenForThisTurn) {
                  safeSetStatus("Listening");
                  markGeminiAnswerReady();
                  // ✅ Clear TTS flags so speech recognition can start
                  window.__TTS_AUDIO_PLAYED__ = false;
                  window.__WAITING_FOR_TTS__ = false;
                  await scheduleRecognitionStart();
                } else {
                  safeSetStatus("AI Speaking...");
                }
                canReceiveQuestionRef.current = true;
                await drainQueuedQuestions();
                browserTtsPlayingRef.current = false;
              };

              window.speechSynthesis.speak(utterance);
              window.__WAITING_FOR_TTS__ = true;
            }

            return;
          }
        } catch (e) {
          // Not JSON, continue
        }

        // ✅ Skip signaling messages
        if (isSignalingMessage(msg)) {
          console.log(
            "🔍 [FILTERED] Signaling message - not displaying to user",
          );

          try {
            const data = JSON.parse(msg);

            // ✅ Handle WebRTC initialization
            if (
              data.type === "init_avatar_stream" ||
              data.type === "webrtc_offer"
            ) {
              console.log("🎬 [WEBRTC] Received stream init from backend");
              const streamData = data.stream_data || data;
              avatarStreamService.handleInitAvatarStream(streamData);

              const answerHandler = (event) => {
                console.log("🎬 [WEBRTC] Answer ready, sending to backend");
                if (
                  socketRef.current &&
                  socketRef.current.readyState === WebSocket.OPEN
                ) {
                  socketRef.current.send(
                    JSON.stringify({
                      type: "sdp_answer",
                      answer: event.answer,
                    }),
                  );
                }
                avatarStreamService.removeListener(
                  "answer-ready",
                  answerHandler,
                );
              };

              avatarStreamService.on("answer-ready", answerHandler);

              const iceHandler = (event) => {
                if (
                  socketRef.current &&
                  socketRef.current.readyState === WebSocket.OPEN
                ) {
                  socketRef.current.send(
                    JSON.stringify({
                      type: "ice_candidate",
                      candidate: event.candidate,
                    }),
                  );
                }
              };

              avatarStreamService.on("ice-candidate", iceHandler);

              // ── Wire up real audio-activity events ─────────────────────────
              // "did-audio-playing" → actual audio energy detected on the stream
              //   → NOW set status to AI Speaking and stop recognition
              // "did-audio-silent"  → audio has gone quiet for >900 ms
              //   → if avatar_speaking_end was already received, transition to Listening

              const didAudioPlayingHandler = () => {
                if (isInterviewTransportInterrupted() || interviewEndedRef.current) return;
                markAudioPlaybackStarted(true);
                if (activeQuestionTextRef.current) {
                  displayAIQuestion(activeQuestionTextRef.current, activeTurnIdRef.current);
                }
                console.log(
                  "🎬 [VoiceInterview] did-audio-playing → status = AI Speaking...",
                );
                safeSetStatus("AI Speaking...");
                window.__WAITING_FOR_TTS__ = true;
                if (recognitionRef.current && recognizingRef.current) {
                  try {
                    recognitionRef.current.stop();
                    recognizingRef.current = false;
                  } catch (e) {}
                }
                try {
                  window.dispatchEvent(
                    new CustomEvent("did_audio_actually_playing"),
                  );
                } catch (e) {}
              };

              const didAudioSilentHandler = () => {
                if (isInterviewTransportInterrupted() || interviewEndedRef.current) return;
                markAudioPlaybackStarted(false);
                console.log("🔇 [VoiceInterview] did-audio-silent fired");
                handleDIDSpeakingFinished();
              };

              avatarStreamService.on(
                "did-audio-playing",
                didAudioPlayingHandler,
              );
              avatarStreamService.on("did-audio-silent", didAudioSilentHandler);

              return;
            }

            // Handle audio_start: reset state and track provider
            if (data.type === "audio_start") {
              if (!isCurrentGeminiAudioFrame(data, true)) {
                console.warn("[AUDIO] Ignoring stale/mismatched Gemini stream start");
                return;
              }
              if (nativeModeRef.current) {
                nativePlaybackAckRef.current = null;
                closingAckSentRef.current = false;
                closingAudioStartedRef.current = false;
              }
              updateFlowError(null);
              armWaitingGuard();
              activeAudioTurnIdRef.current = data.turn_id || null;
              activeAudioQuestionRef.current = typeof data.question === "string" ? data.question.trim() : "";
              if (Number.isFinite(data.stream_sequence)) {
                lastAudioStreamSequenceRef.current = data.stream_sequence;
              }
              // A fresh stream replaces the prior attempt, including replay.
              audioStreamGenerationRef.current += 1;
              activeAudioStreamIdRef.current = data.stream_id || null;
              stopSharedAIPlayback();
              stopTrackedAudioSources();
              markAudioPlaybackStarted(false);
              cancelRecoveryReplay();
              console.log(
                `[GEMINI DIAGNOSTIC] New Gemini response started (audio_start received, provider=${data.provider})`,
              );
              console.log(
                `🎵 [AUDIO_START] Received audio start for provider: ${data.provider}`,
              );
              beginAssistantTurn();
              nativeListenAllowedRef.current = false;
              flushGeminiAudioBuffer();
              currentAudioProviderRef.current = data.provider;
              if (data.provider === "gemini") {
                isGeminiLiveRef.current = true;
              }
              // If the backend included the question text with audio_start, display it
              if (data.provider === "gemini") {
                isGeminiLiveRef.current = true;

                const geminiQuestion =
                  typeof data.question === "string" ? data.question.trim() : "";

                if (geminiQuestion) {
                  console.log(
                    "🎤 [GEMINI] Question received in audio_start:",
                    geminiQuestion.substring(0, 120),
                  );

                  // Store only. UI will be updated when Gemini audio playback starts.
                  pendingQuestionRef.current = geminiQuestion;
                }
              }
              // Prevent Gemini mic capture from starting before playback begins
              window.__WAITING_FOR_TTS__ = true;
              window.__TTS_AUDIO_PLAYED__ = false;
              pendingAnswerRef.current = false;
              geminiSpeechDetectedRef.current = false;
              geminiSpeechFramesRef.current = 0;
              geminiSpeechActiveRef.current = false;
              if (geminiSpeechEndTimeoutRef.current) {
                clearTimeout(geminiSpeechEndTimeoutRef.current);
                geminiSpeechEndTimeoutRef.current = null;
              }
              if (recognitionRef.current && recognizingRef.current) {
                try {
                  recognitionRef.current.stop();
                } catch (e) {}
                recognizingRef.current = false;
              }
              // Reset state for new audio stream
              audioQueueRef.current = [];
              lastProcessedIndexRef.current = -1;
              nextPlayTimeRef.current = 0;
              geminiPlaybackStartTimeRef.current = 0;
              geminiPcmQueueRef.current = [];
      geminiActiveBatchesRef.current.clear();
              geminiStreamStartedRef.current = false;
              audioStreamFinalizedRef.current = false;
              isPlayingStreamingRef.current = false;
              geminiTtsStartedRef.current = false;
              return;
            }

            // Handle audio chunks — only in static/browser mode.
            // In D-ID mode the backend sends NO audio_chunk messages;
            // the avatar's WebRTC video stream carries the audio.
            // In custom mode, audio_chunk messages come from the GPU AUDIO: frames
            // and are forwarded to the CustomAvatarDisplay for playback.
            if (data.type === "audio_chunk") {
              if (!isCurrentGeminiAudioFrame(data)) return;
              if (data.stream_id && data.stream_id !== activeAudioStreamIdRef.current) {
                console.warn("[AUDIO] Ignoring stale stream frame", data.type);
                return;
              }
              cancelRecoveryReplay();
              if (avatarModeRef.current === "did") {
                return;
              }
              if (avatarModeRef.current === "custom") {
                // Route audio to CustomAvatarDisplay via AvatarStreamService
                if (data.data && data.data.length > 0) {
                  avatarStreamService.handleCustomAvatarAudio(data.data);
                }
                return;
              }
              if (data.data && data.data.length > 0) {
                // Route to appropriate queue based on provider
                if (data.provider === "gemini") {
                  if (currentAudioProviderRef.current !== "gemini") {
                    currentAudioProviderRef.current = "gemini";
                  }
                  if (isGeminiLiveRef.current !== true) {
                    isGeminiLiveRef.current = true;
                  }
                  const decodedChunk =
                    typeof data.data === "string"
                      ? decodeGeminiBase64Chunk(data.data)
                      : data.data;
                  console.log(
                    `[GEMINI DIAGNOSTIC] Received audio_chunk, decoded chunk length=${decodedChunk.length}, queue size=${geminiPcmQueueRef.current.length + 1}`,
                  );
                  geminiPcmQueueRef.current.push(decodedChunk);
                  if (!isPlayingStreamingRef.current) {
                    safeSetStatus("AI Speaking...");
                    processNextAudioBatch();
                  } else {
                    // If already playing, try to process next batch immediately if enough chunks
                    processNextAudioBatch();
                  }
                } else {
                  // Legacy path (unchanged)
                  audioQueueRef.current.push(data);
                  if (!isPlayingStreamingRef.current) {
                    safeSetStatus("AI Speaking...");
                    processNextAudioBatch();
                  }
                }
              }
              return;
            }

            // ✅ Handle avatar video mode
            if (data.type === "avatar_video") {
              console.log("🎬 [AVATAR] Received avatar_video config");
              avatarStreamService.handleAvatarVideo(data);
              return;
            }

            // ✅ Handle custom avatar ready signal
            if (data.type === "custom_avatar_ready") {
              console.log("🎬 [FRONTEND] Custom avatar READY");
              avatarModeRef.current = "custom"; // ← keep ref in sync
              avatarStreamService.emit("mode-changed", { mode: "custom" });
              return;
            }

            // ✅ Handle custom avatar video frame
            if (data.type === "custom_avatar_video_frame") {
              console.log(
                "🎬 [CUSTOM AVATAR] Received frame:",
                data.frame_index,
              );
              avatarStreamService.handleCustomAvatarFrame(
                data.frame_data,
                data.frame_index,
              );
              return;
            }

            // ── avatar_speaking_start ─────────────────────────────────────────
            // D-ID fires this BEFORE the audio reaches the client over WebRTC.
            // We ONLY use it to: (a) block recognition early, (b) set the
            // waiting flag.  We do NOT set status to "AI Speaking..." here —
            // that happens when we actually detect audio energy on the stream.
            if (data.type === "avatar_speaking_start") {
              cancelRecoveryReplay();
              console.log(
                "🎬 [VoiceInterview] avatar_speaking_start — blocking recognition (audio not yet playing)",
              );
              window.__WAITING_FOR_TTS__ = true;
              window.__DID_SPEAKING_START_RECEIVED__ = true; // flag for did-audio-silent guard
              if (recognitionRef.current && recognizingRef.current) {
                try {
                  recognitionRef.current.stop();
                  recognizingRef.current = false;
                } catch (e) {}
              }
              // Let AIPanel know to stay in "waiting" state (not flip to speaking yet)
              try {
                window.dispatchEvent(new CustomEvent("avatar_speaking_start"));
              } catch (e) {}
              return;
            }

            // ── avatar_speaking_end ───────────────────────────────────────────
            // D-ID fires this BEFORE the audio finishes playing on the client.
            // We record the flag but do NOT transition to Listening yet.
            // The actual transition happens in the "did-audio-silent" handler
            // inside the avatarStreamService event subscription below, which
            // fires only after real silence is detected on the media stream.
            if (data.type === "avatar_speaking_end") {
              console.log(
                "🎬 [VoiceInterview] avatar_speaking_end — audio may still be playing, waiting for silence",
              );
              window.__DID_SPEAKING_END_RECEIVED__ = true;
              // Notify AIPanel so it starts its own drain guard
              try {
                window.dispatchEvent(new CustomEvent("avatar_speaking_end"));
              } catch (e) {}
              // Safety fallback: if silence detection never fires (e.g. analyser
              // not available), transition after a generous 4-second window.
              if (didSpeakingEndFallbackRef.current)
                clearTimeout(didSpeakingEndFallbackRef.current);
              didSpeakingEndFallbackRef.current = setTimeout(() => {
                if (!isMountedRef.current) return;
                if (window.__DID_SPEAKING_END_RECEIVED__) {
                  console.warn(
                    "🎬 [VoiceInterview] avatar_speaking_end fallback fired — silence detection may have missed",
                  );
                  handleDIDSpeakingFinished();
                }
              }, 4000);
              return;
            }

            // ✅ Handle audio complete
            if (data.type === "audio_complete") {
              console.log(
                `🎉 [TTS COMPLETE] Total chunks: ${data.chunks_sent}`,
              );
              // audio_complete only describes provider delivery. It does not
              // prove that the browser finished playing the audio. Playback
              // callbacks/audio_end own the AI-speaking -> Listening transition;
              // advancing here can capture the remaining question as an answer
              // after a reconnect.
              return;
            }

            // ✅ Handle corrected transcript
            if (data.type === "corrected_transcript") {
              setMessages((prev) => {
                const updated = [...prev];
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i].answer?.startsWith("🧑 You:")) {
                    updated[i].answer = "🧑 You: " + data.text;
                    if (updated[i].isSubmitted !== true)
                      updated[i].isSubmitted = true;
                    break;
                  }
                }
                return updated;
              });
              return;
            }

            if (data.type === "answer_complete") {
              const transcriptText = (data.text || "").trim();
              if (transcriptText) {
                setMessages((prev) => {
                  const updated = [...prev];
                  let foundIndex = -1;
                  for (let i = updated.length - 1; i >= 0; i--) {
                    if (
                      updated[i].answer?.startsWith("🧑 You:") &&
                      !updated[i].isSubmitted
                    ) {
                      foundIndex = i;
                      break;
                    }
                  }
                  if (foundIndex >= 0) {
                    updated[foundIndex].answer = "🧑 You: " + transcriptText;
                    updated[foundIndex].isSubmitted = true;
                  } else {
                    updated.push({
                      answer: "🧑 You: " + transcriptText,
                      isSubmitted: true,
                    });
                  }
                  return updated;
                });
              }
              return;
            }

            // Handle audio_end - stream is done sending, playback may still be in progress
            if (data.type === "audio_end") {
              if (!isCurrentGeminiAudioFrame(data)) return;
              if (data.stream_id && data.stream_id !== activeAudioStreamIdRef.current) {
                console.warn("[AUDIO] Ignoring stale stream frame", data.type);
                return;
              }
              const isGemini = currentAudioProviderRef.current === "gemini";
              const queueSize = isGemini
                ? geminiPcmQueueRef.current.length
                : audioQueueRef.current.length;
              console.log(
                `[GEMINI DIAGNOSTIC] audio_end received, queue size=${queueSize}, provider=${currentAudioProviderRef.current}`,
              );
              console.log(
                `🎉 [AUDIO_END] Stream complete. Chunks queued: ${queueSize}, playing: ${isPlayingStreamingRef.current}`,
              );

              // Mark finalized so processNextAudioBatch transitions to Listening after last chunk
              audioStreamFinalizedRef.current = true;

              // Always call processNextAudioBatch for Gemini to process remaining chunks
              if (isGemini) {
                console.log(
                  `[GEMINI DIAGNOSTIC] Calling processNextAudioBatch after audio_end`,
                );
                if (
                  !isPlayingStreamingRef.current ||
                  geminiPcmQueueRef.current.length > 0
                ) {
                  processNextAudioBatch();
                } else {
                  console.log(
                    `[GEMINI DIAGNOSTIC] audio_end received while Gemini playback still active; waiting for current batch to finish`,
                  );
                }
              } else {
                // If chunks arrived but playback hasn't started yet, kick it off now
                if (
                  !isPlayingStreamingRef.current &&
                  audioQueueRef.current.length > 0
                ) {
                  console.log(
                    "🎵 [AUDIO_END] Starting playback of buffered chunks...",
                  );
                  window.__TTS_AUDIO_PLAYED__ = true;
                  safeSetStatus("AI Speaking...");
                  processNextAudioBatch();
                }

                // If no chunks at all (silent/empty response), switch to Listening immediately
                if (
                  audioQueueRef.current.length === 0 &&
                  !isPlayingStreamingRef.current
                ) {
                  console.log(
                    "🎵 [AUDIO_END] No audio chunks — switching to Listening immediately",
                  );
                  window.__WAITING_FOR_TTS__ = false;
                  window.__TTS_AUDIO_PLAYED__ = false;
                  if (ttsDoneResolverRef.current) {
                    try {
                      clearTimeout(ttsDoneResolverRef.current.__timeoutId);
                    } catch (e) {}
                    const resolver = ttsDoneResolverRef.current;
                    ttsDoneResolverRef.current = null;
                    resolver();
                  }
                  audioStreamFinalizedRef.current = false;
                  if (currentTurnShouldListenRef.current) {
                    safeSetStatus("Listening");
                    markGeminiAnswerReady();
                    if (!recognitionRef.current) initRecognition();
                    // Fix 7: scheduleRecognitionStart handles mic release + delay + start
                    scheduleRecognitionStart();
                  } else {
                    safeSetStatus("AI Speaking...");
                    canReceiveQuestionRef.current = true;
                    drainQueuedQuestions();
                  }
                }
              }

              return;
            }
          } catch (e) {
            console.error("❌ Error parsing signaling message:", e);
          }

          return;
        }

        // ✅ Process only actual question/message content
        try {
          const data = JSON.parse(msg);
        } catch (e) {
          // Not JSON, continue
        }

        if (msg.includes("Say 'Start Interview'")) {
          if (isPausedRef.current) return;
          setMessages((prev) => [...prev, { question: "👤 AI: " + msg }]);
          pendingCommandRef.current = true;
          return;
        }

        if (msg === "[[END]]") {
          let finalQ = chunkBufferRef.current.trim();
          chunkBufferRef.current = "";

          finalQ = finalQ
            .replace(
              /\{"type"\s*:\s*"(avatar_segment|audio_stream_start|avatar_speaking_start|avatar_speaking_end|audio_chunk|audio_complete)"[^}]*\}/g,
              "",
            )
            .trim();

          console.log("📋 [QUESTION] [[END]] received:", {
            question: finalQ.substring(0, 120),
            length: finalQ.length,
            provider: currentAudioProviderRef.current,
          });

          if (!finalQ) {
            console.warn(
              "⚠️ [QUESTION] [[END]] received but question is empty",
            );
            return;
          }

          cancelRecoveryReplay(finalQ);

          if (isPausedRef.current) {
            return;
          }

          // IMPORTANT:
          // Display the question independently of the TTS provider.
          pendingQuestionRef.current = finalQ;

          // Text arrives before TTS. Retain it until actual playback starts.
          activeQuestionTextRef.current = finalQ;
          // Custom avatar playback is owned by a separate component, whose
          // audio-start callback is not available here. Preserve its display.
          if (avatarModeRef.current === "custom") displayAIQuestion(finalQ);

          // If another question is currently being processed,
          // queue this question for later.
          if (!canReceiveQuestionRef.current) {
            console.log("📋 [QUEUE] Question queued:", finalQ.substring(0, 80));

            questionQueueRef.current.push(finalQ);
            return;
          }

          canReceiveQuestionRef.current = false;
          pendingQuestionRef.current = finalQ;
          questionDisplayedRef.current = false;
          lastSentInterimRef.current = "";
          audioStreamFinalizedRef.current = false;

          speak(
            finalQ,
            async () => {
              canReceiveQuestionRef.current = true;
              await drainQueuedQuestions();
            },
            () => {},
          );

          return;
        }

        if (msg === "enable compiler") {
          handleCompilerHandoff();
          console.log(
            "✅ [COMPILER ENABLED] Backend sent enable compiler message",
          );
          if (onCompilerEnabled) {
            onCompilerEnabled(true);
          }
          return;
        }

        if (!isPausedRef.current) {
          chunkBufferRef.current += msg;
        }
      };

      socket.onerror = (error) => {
        console.error("❌ WebSocket error:", error);

        if (!isCurrentSocket()) {
          console.warn("[WS] stale socket callback ignored: onerror", socketGeneration);
          return;
        }

        if (interviewEndedRef.current) {
          return;
        }

        if (codingHandoffRef.current) {
          clearSocketConnectTimeout();
          stopSocketHeartbeat();
          return;
        }

        reconnectInFlightRef.current = false;
        interruptInterviewConnection();
      };

      socket.onclose = () => {
        if (!isCurrentSocket()) {
          console.warn("[WS] stale socket callback ignored: onclose", socketGeneration);
          return;
        }
        if (interviewEndedRef.current) return;
        clearSocketConnectTimeout();
        reconnectInFlightRef.current = false;
        socketRef.current = null;
        if (codingHandoffRef.current) {
          stopSocketHeartbeat();
          return;
        }
        showConnectionInterrupted();
        stopSocketHeartbeat();
        console.log(
          "🔒 WebSocket closed; recognition lifecycle permanently stopped",
        );
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        console.log("🔌 WebSocket closed");
        stopInterviewAudioForDisconnect("socket-close");

        appendMessage("❌ Disconnected from Server.");

        safeSetStatus("Reconnecting");
        scheduleReconnect();
      };
    };

    useEffect(() => {
      if (role === "admin") {
        const socket = new WebSocket(
          `${WS_BASE}/interview/${interviewId}?role=admin`,
        );
        socketRef.current = socket;

        socket.onopen = () => {
          appendMessage("👀 Connected to server as admin. Monitoring only.");
          safeSetStatus("Monitoring");
        };

        socket.onmessage = (evt) => {
          const msg = evt.data;
          setMessages((prev) => [
            ...prev,
            { question: "👤 Candidate: " + msg },
          ]);
        };

        socket.onerror = (e) => {
          appendMessage("❌ Connection error.");
          safeSetStatus("Error");
        };

        socket.onclose = () => {
          appendMessage("❌ Disconnected from Server.");
          safeSetStatus("Disconnected");
        };

        return () => {
          socketRef.current?.close();
        };
      } else {
        if (!languageReady) return undefined;
        interviewEndedRef.current = interviewEnded;
        startInterview();

        return () => {
          interviewEndedRef.current = true;
          console.log("🔒 VoiceInterview cleanup: interview lifecycle stopped");
          socketRef.current?.close();
          socketRef.current = null;
          clearSocketConnectTimeout();
          stopSocketHeartbeat();
          clearConnectionUiTimers();
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
          if (recoveryReplayTimerRef.current) {
            clearTimeout(recoveryReplayTimerRef.current);
            recoveryReplayTimerRef.current = null;
          }
          recoveryTurnRef.current = null;

          if (recognitionRef.current) {
            try {
              recognitionRef.current.stop();
            } catch (e) {}
          }
          recognizingRef.current = false;

          if (silenceTimeoutRef.current)
            clearTimeout(silenceTimeoutRef.current);

          clearTtsKeepAlive();
          window.speechSynthesis.cancel();
          selectedBrowserTtsVoiceRef.current = null;
          selectedBrowserTtsGenderRef.current = null;
          stopGeminiAudioCapture();

          if (audioRef.current) {
            try {
              audioRef.current.stop();
            } catch (e) {}
            audioRef.current = null;
          }

          audioContextRef.current = null;
          aiDestinationRef.current = null;

          try {
            avatarStreamService.cleanup();
          } catch (e) {
            console.warn("Error cleaning up AvatarStreamService:", e);
          }
        };
      }
    }, [interviewId, role, languageReady]);

    useEffect(() => {
      if (role === "admin" || !isMountedRef.current) return;

      let listeningStartTime = null;
      const monitorInterval = setInterval(() => {
        if (codingHandoffRef.current) return;
        if (
          pendingAnswerRef.current &&
          !isPlayingStreamingRef.current &&
          !window.__WAITING_FOR_TTS__
        ) {
          if (!listeningStartTime) {
            listeningStartTime = Date.now();
          }

          const listeningDuration = Date.now() - listeningStartTime;
          if (!recognizingRef.current && listeningDuration > 5000) {
            console.warn(
              "🎤 Recognition stuck for 5+ seconds in listening mode, forcing reset",
            );
            forceRecognitionReset();
            listeningStartTime = Date.now();
          }
        } else {
          listeningStartTime = null;
        }

        ensureRecognitionActive();
      }, 1500);

      return () => clearInterval(monitorInterval);
    }, [role]);

    useEffect(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, [messages]);

    // Presentation-only buffering state derived from the existing interview
    // status. Candidate listening remains fully interactive and never buffers.
    const isBuffering =
      role !== "admin" &&
      connectionUi.state === "connected" && !audioPlaying && !flowError &&
      (status === "Interview Starting..." || status === "AI Speaking..." || status === "Please wait...");
    const bufferingMessage =
      status === "Interview Starting..."
        ? "Preparing your interview..."
        : "Preparing your next question...";

    return (
      <div className="relative flex flex-col h-full bg-white dark:bg-gray-900">
        {flowError && role !== "admin" && (
          <div role="alert" className="absolute inset-x-4 top-16 z-50 rounded-xl border border-amber-300 bg-white p-4 shadow-lg dark:bg-gray-800">
            <p className="text-sm text-gray-800 dark:text-gray-100">{flowError.message}</p>
            <div className="mt-3 flex gap-3">
              {flowError.retryable && <button type="button" onClick={retryCurrentAudio} className="rounded bg-teal-600 px-3 py-2 text-sm text-white">Retry audio</button>}
              {flowError.code === "WAITING_FOR_RESPONSE" && <button type="button" onClick={() => {updateFlowError(null); safeSetStatus("Please wait..."); armWaitingGuard();}} className="rounded border px-3 py-2 text-sm">Keep waiting</button>}
              <button type="button" onClick={() => endInterview?.()} className="rounded border px-3 py-2 text-sm">End Interview</button>
            </div>
          </div>
        )}
        {isBuffering && (
          <div
            className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-white/60 px-6 backdrop-blur-[1px] dark:bg-gray-950/60"
            role="status"
            aria-live="polite"
          >
            <div className="rounded-2xl bg-white/95 px-6 py-5 text-center shadow-lg dark:bg-gray-800/95">
              <div
                className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600 dark:border-gray-600 dark:border-t-teal-400"
                aria-hidden="true"
              />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {bufferingMessage}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Please wait...
              </p>
            </div>
          </div>
        )}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 shadow-md">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  status === "End Interview"
                    ? "bg-red-500"
                    : status === "Listening"
                    ? "bg-green-500 animate-pulse"
                    : status === "AI Speaking..."
                      ? "bg-purple-500 animate-pulse"
                      : status === "Interview Starting..."
                        ? "bg-blue-500 animate-pulse"
                        : "bg-gray-400"
                }`}
              ></div>
              <span className="font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">
                Status
              </span>
            </div>
            {status && (
              <span
                className={`font-bold text-xs px-3 py-1.5 rounded-full backdrop-blur-sm border ${
                  status === "End Interview"
                    ? "bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300"
                    : status === "Listening"
                    ? "bg-green-50 dark:bg-green-950/40 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                    : status === "AI Speaking..."
                      ? "bg-purple-50 dark:bg-purple-950/40 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300"
                      : status === "Interview Starting..."
                        ? "bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300"
                        : "bg-gray-50 dark:bg-gray-950/40 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                }`}
              >
                {status}
              </span>
            )}
          </div>
          {countdown > 0 && (
            <div className="text-center text-xs text-gray-600 dark:text-gray-400 mt-2 font-medium">
              ⏳ Next question in {countdown}s
            </div>
          )}
        </div>

        <div
          id="interview-transcript"
          ref={containerRef}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50 dark:bg-gray-950"
        >
          {messages.map(
            (msg, index) =>
              (msg.question || msg.answer) && (
                <div key={index} className="animate-fadeIn space-y-2">
                  {msg.answer && (
                    <div className="bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100 leading-relaxed break-words">
                        {msg.answer}
                      </p>
                    </div>
                  )}
                  {msg.question && (
                    <div className="bg-teal-50 dark:bg-teal-950/40 border-l-4 border-teal-500 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <p className="text-sm font-medium text-teal-900 dark:text-teal-100 leading-relaxed break-words">
                        {msg.question}
                      </p>
                    </div>
                  )}
                </div>
              ),
          )}

          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-16 h-16 bg-teal-100/50 dark:bg-teal-900/30 rounded-full flex items-center justify-center mb-4 animate-pulse">
                <svg
                  className="w-8 h-8 text-teal-600 dark:text-teal-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4"
                  />
                </svg>
              </div>
              <p className="text-teal-700 dark:text-teal-300 font-medium mb-1">
                Ready to start
              </p>
              <p className="text-teal-600 dark:text-teal-400 text-xs">
                Waiting for AI to begin the interview...
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-5 py-2"></div>

        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style>
      </div>
    );
  },
);

export default VoiceInterview;
